import { useEffect, useMemo, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight,
  List,
  X,
  TwitterLogo,
  LinkedinLogo,
  FacebookLogo,
  RedditLogo,
  ArrowLeft,
  SignOut,
  Plus,
  PencilSimple,
  Trash,
  Eye,
  EyeSlash,
  Gear,
  Lock,
  Tag,
  ListBullets,
  ListNumbers,
  LinkSimple,
  CalendarBlank,
  User,
  CreditCard,
  Rocket,
  CheckCircle,
  XCircle,
  EnvelopeSimple,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import { PrivacyPolicyPage, TermsPage } from "./pages/LegalPage";
import { DirectoryList } from "./components/DirectoryList";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auto-match platform name → hosted logo. Used as a fallback when a job row
// doesn't already have icon_url set. Keys are compared case-insensitively.
const PLATFORM_ICON_MAP = {
  "micro1": "/icons/micro1.png",
  "mercor": "/icons/mercor.png",
  "afterquery": "/icons/afterquery.png",
  "turing": "/icons/turing.png",
  "sme careers": "/icons/sme.jpeg",
  "ixolabs": "/icons/ixolabs.jpeg",
  "alignerr": "/icons/alignerr.png",
  "handshake ai": "/icons/handshake.png",
  "remo experts": "/icons/remo.png",
  "ethos": "/icons/ethos.webp",
  "vetto": "/icons/vetto.webp",
  "silencio": "/icons/silencio.jpeg",
  "workbolt": "/icons/workbolt.png",
  "terac": "/icons/terac.png",
  "cleverx": "/icons/cleverx.png",
  "crowdgen by appen": "/icons/crowdgen.png",
  "crowdgen": "/icons/crowdgen.png",
  "uberai solutions": "/icons/uberai.png",
  "uberai": "/icons/uberai.png",
  "transperfect dataforce": "/icons/dataforce.jpeg",
  "dataforce": "/icons/dataforce.jpeg",
  "task verse": "/icons/taskverse.jpeg",
  "taskverse": "/icons/taskverse.jpeg",
  "oneforma": "/icons/oneforma.jpeg",
};

const resolvePlatformIcon = (job) => {
  if (job?.icon_url) return job.icon_url;
  const key = (job?.platform || "").trim().toLowerCase();
  return PLATFORM_ICON_MAP[key] || "";
};


const SECTION_OPTIONS = [
  { value: "all", label: "All sections" },
  { value: "expert-networks", label: "Expert Networks" },
  { value: "one-time-studies", label: "One-time Studies" },
  { value: "voice-ai", label: "Voice AI" },
  { value: "micro-tasks", label: "Micro-task Platforms" },
];

const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const slugify = (value = "") => value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const sectionLabel = (value) => SECTION_OPTIONS.find((item) => item.value === value)?.label || value;

const PLATFORM_SECTIONS = SECTION_OPTIONS.filter((option) => option.value !== "all");

const EMPTY_PLATFORM_FORM = {
  section: "expert-networks",
  name: "",
  platform: "",
  badge: "",
  icon: "",
  description: "",
  href: "",
  meta: "",
  note: "",
  action: "jobs",
  order: 0,
  active: true,
};

const EMPTY_DIRECTORY_FORM = {
  name: "",
  url: "",
  category: "AI Training Platforms",
  button_label: "Visit site",
  order: 0,
  active: true,
};

const HOMEPAGE_FIELDS = [
  { key: "hero_eyebrow", label: "Hero eyebrow" },
  { key: "hero_title_lead", label: "Hero title — start" },
  { key: "hero_title_highlight", label: "Hero title — highlighted words" },
  { key: "hero_title_tail", label: "Hero title — end" },
  { key: "hero_description", label: "Hero description", long: true },
  { key: "expert_title", label: "Expert networks — heading" },
  { key: "expert_description", label: "Expert networks — description", long: true },
  { key: "studies_title", label: "One-time studies — heading" },
  { key: "studies_description", label: "One-time studies — description", long: true },
  { key: "voice_title", label: "Voice AI — heading" },
  { key: "voice_description", label: "Voice AI — description", long: true },
  { key: "micro_title", label: "Micro-tasks — heading" },
  { key: "micro_description", label: "Micro-tasks — description", long: true },
  { key: "jobs_title", label: "Jobs table — heading" },
  { key: "jobs_description", label: "Jobs table — description", long: true },
  { key: "blog_title", label: "Blog preview — heading" },
  { key: "blog_description", label: "Blog preview — description", long: true },
];

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("admin_token"));
  const [admin, setAdmin] = useState(null);

  const login = (newToken) => {
    localStorage.setItem("admin_token", newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
    setAdmin(null);
  };

  return <AuthContext.Provider value={{ token, admin, setAdmin, login, logout }}>{children}</AuthContext.Provider>;
};

const bootstrapSite = async () => {
  await Promise.allSettled([
    axios.post(`${API}/seed`),
    axios.post(`${API}/admin/setup`),
    axios.post(`${API}/jobs/seed-sample`),
  ]);
};

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [site, setSite] = useState(null);
  const location = useLocation();
  const navItems = site?.menu_items?.length
    ? [...site.menu_items].sort((a, b) => (a.order || 0) - (b.order || 0)).map((item) => ({ label: item.label, url: item.url }))
    : [
        { label: "Home", url: "/" },
        { label: "Jobs", url: "/?focus=jobs" },
        { label: "Post a Job", url: "/post-a-job" },
        { label: "Newsletter", url: "/newsletter" },
        { label: "Blog", url: "/blog" },
        { label: "About Us", url: "/about" },
      ];

  useEffect(() => {
    axios.get(`${API}/settings`).then((res) => setSite(res.data)).catch(() => {});
  }, []);

  const isActive = (url) => {
    if (url === "/") return location.pathname === "/" && !location.search.includes("focus=jobs");
    if (url.includes("focus=jobs")) return location.pathname === "/" && location.search.includes("focus=jobs");
    return location.pathname === url;
  };

  return (
    <header className="header-glass fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0" data-testid="logo-link">
            <img src="/logo.png" alt="TheOTAutomations" className="w-11 h-11 rounded-xl object-cover shrink-0" data-testid="site-logo-image" />
            <div className="min-w-0">
              <div className="text-white font-semibold truncate" style={{ fontFamily: "Unbounded, sans-serif" }}>
                {site?.site_name || "theotautomations.com"}
              </div>
              <div className="text-xs text-[#A1A1AA] truncate">{site?.tagline || "AI training jobs board"}</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-3" data-testid="desktop-nav">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.url}
                className={`nav-chip ${isActive(item.url) ? "nav-chip-active" : ""}`}
                data-testid={`nav-${slugify(item.label)}`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <button
            onClick={() => setIsOpen((prev) => !prev)}
            className="md:hidden text-white p-2 rounded-full border border-white/10"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={20} /> : <List size={20} />}
          </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden pb-4"
              data-testid="mobile-nav-panel"
            >
              <div className="grid gap-2">
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    to={item.url}
                    onClick={() => setIsOpen(false)}
                    className="nav-chip"
                    data-testid={`mobile-nav-${slugify(item.label)}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

const PlatformMarquee = () => (
  <div className="bg-[#0A0B12] border-y border-white/10 py-3 overflow-hidden" data-testid="platform-marquee">
    <Marquee speed={40} gradient={false}>
      {["AfterQuery", "Alignerr", "Handshake AI", "Ixolabs", "Ethos", "Micro1", "Mercor", "Silencio", "Task Verse"].map(
        (platform) => (
          <div key={platform} className="flex items-center">
            <span className="text-[#A1A1AA] text-sm mx-6 font-mono uppercase tracking-[0.25em]">{platform}</span>
            <span className="text-[#D9F839] mx-2">✦</span>
          </div>
        )
      )}
    </Marquee>
  </div>
);

const SectionHeader = ({ eyebrow, title, description, testId }) => (
  <div className="max-w-3xl mb-10" data-testid={testId}>
    <h2 className="text-3xl md:text-4xl text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>
      {title}
    </h2>
    <p className="text-sm md:text-base text-[#A1A1AA] max-w-2xl leading-relaxed">{description}</p>
  </div>
);

const PlatformIcon = ({ badge, iconSrc, testId }) => (
  <div className="platform-icon-shell" data-testid={testId}>
    {iconSrc ? (
      <img src={iconSrc} alt={badge || "icon"} className="w-full h-full object-contain rounded-md" />
    ) : (
      badge
    )}
  </div>
);

const PlatformCard = ({ item, count = 0, onJobs, cardTestId, actionTestId }) => (
  <div className="panel-card p-5 flex flex-col gap-5 h-full" data-testid={cardTestId}>
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0">
        <PlatformIcon badge={item.badge} iconSrc={item.icon} testId={`${slugify(item.name)}-icon`} />
        <div className="min-w-0">
          <h3 className="text-white text-lg mb-1 leading-tight">{item.name}</h3>
          <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.description}</p>
        </div>
      </div>
      {item.action === "jobs" && (
        count > 0 ? (
          <span className="stats-chip shrink-0" data-testid={`${slugify(item.name)}-count`}>{count} jobs</span>
        ) : (
          <span className="stats-chip opacity-80 shrink-0" data-testid={`${slugify(item.name)}-status`}>Coming soon</span>
        )
      )}
    </div>
    {item.meta && <div className="text-xs text-[#D9F839]" data-testid={`${slugify(item.name)}-meta`}>{item.meta}</div>}
    {item.note && <div className="text-xs text-[#A1A1AA]" data-testid={`${slugify(item.name)}-note`}>{item.note}</div>}
    {item.action === "jobs" ? (
      <button
        className="cta-button rounded-full px-5 py-3 w-full flex items-center justify-center gap-2 mt-auto"
        onClick={() => onJobs?.(item)}
        data-testid={actionTestId}
      >
        See jobs <ArrowUpRight size={16} weight="bold" />
      </button>
    ) : item.href ? (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        className="cta-button rounded-full px-5 py-3 w-full flex items-center justify-center gap-2 mt-auto"
        data-testid={actionTestId}
      >
        Visit platform <ArrowUpRight size={16} weight="bold" />
      </a>
    ) : (
      <div className="stats-chip mt-auto text-center" data-testid={`${actionTestId}-disabled`}>Coming soon</div>
    )}
  </div>
);

const PlatformSection = ({ id, title, description, entries, gridClass, cardPrefix, actionPrefix, headerTestId, platformCounts = {}, onJobs }) => {
  if (!entries.length) return null;
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid={id}>
      <div className="max-w-7xl mx-auto">
        <SectionHeader title={title} description={description} testId={headerTestId} />
        <div className={`grid gap-4 ${gridClass}`}>
          {entries.map((item) => (
            <PlatformCard
              key={item.id || item.name}
              item={item}
              count={platformCounts[item.platform || item.name] || 0}
              onJobs={onJobs}
              cardTestId={`${cardPrefix}-${slugify(item.name)}`}
              actionTestId={`${actionPrefix}-${slugify(item.name)}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const BlogCard = ({ post, index, featured = false }) => (
  <motion.article
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.08 }}
    className={`blog-card panel-card overflow-hidden ${featured ? "md:col-span-2 md:row-span-2" : ""}`}
    data-testid={`blog-post-card-${post.slug}`}
  >
    <Link to={`/post/${post.slug}`}>
      <div className={`relative overflow-hidden ${featured ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-transparent to-transparent" />
      </div>
      <div className="p-6">
        <div className="flex gap-2 mb-3 flex-wrap">
          <span className="tag px-2 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full">{post.category}</span>
        </div>
        <h2 className={`text-white mb-2 ${featured ? "text-2xl md:text-3xl" : "text-lg"}`} style={{ fontFamily: "Unbounded, sans-serif" }}>
          {post.title}
        </h2>
        <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-3">{post.excerpt}</p>
        <div className="flex items-center gap-4 text-xs text-[#A1A1AA] mb-3">
          <span className="flex items-center gap-1"><User size={12} />{post.author || "Anonymous AI Guru"}</span>
          <span className="flex items-center gap-1"><CalendarBlank size={12} />{formatDate(post.created_at)}</span>
        </div>
        <div className="flex items-center gap-2 text-[#D9F839] text-sm font-medium">Read More <ArrowUpRight size={16} /></div>
      </div>
    </Link>
  </motion.article>
);

const SocialShare = ({ title, url }) => {
  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="social-share-buttons">
      <span className="text-[#A1A1AA] text-sm">Share:</span>
      <a href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="social-btn rounded-full" data-testid="share-twitter"><TwitterLogo size={20} weight="fill" /></a>
      <a href={`https://www.reddit.com/submit?url=${shareUrl}&title=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="social-btn rounded-full" data-testid="share-reddit"><RedditLogo size={20} weight="fill" /></a>
      <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="social-btn rounded-full" data-testid="share-linkedin"><LinkedinLogo size={20} weight="fill" /></a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="social-btn rounded-full" data-testid="share-facebook"><FacebookLogo size={20} weight="fill" /></a>
    </div>
  );
};

const ReferralButton = ({ link }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    className="referral-card block p-6 mb-4 rounded-2xl hover:border-[#D9F839]"
    data-testid={`referral-link-${slugify(link.name)}`}
  >
    <div className="flex items-center justify-between gap-4">
      <div>
        <h3 className="text-white text-lg font-bold mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>{link.name}</h3>
        <p className="text-[#A1A1AA] text-sm">{link.description}</p>
        {link.bonus && <span className="inline-block mt-2 px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] text-sm font-mono rounded-full">Bonus: {link.bonus}</span>}
      </div>
      <div className="cta-button px-6 py-3 rounded-full flex items-center gap-2 shrink-0">Join <ArrowUpRight size={18} weight="bold" /></div>
    </div>
  </a>
);

const VideoEmbed = ({ url }) => {
  if (!url) return null;

  let embedUrl = url;
  if (url.includes("youtube.com/watch")) {
    const videoId = url.split("v=")[1]?.split("&")[0];
    embedUrl = `https://www.youtube.com/embed/${videoId}`;
  } else if (url.includes("youtu.be/")) {
    const videoId = url.split("youtu.be/")[1]?.split("?")[0];
    embedUrl = `https://www.youtube.com/embed/${videoId}`;
  } else if (url.includes("vimeo.com/")) {
    const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
    embedUrl = `https://player.vimeo.com/video/${videoId}`;
  }

  return (
    <div className="my-8 aspect-video rounded-2xl overflow-hidden border border-white/10" data-testid="video-embed">
      <iframe src={embedUrl} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Video" />
    </div>
  );
};

const formatText = (text) => {
  const parts = text.split(/(\*\*.*?\*\*|\[.*?\]\(.*?\)|`.*?`)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
    if (part.match(/\[.*?\]\(.*?\)/)) {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      return <a key={i} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-[#D9F839] hover:text-[#F4FF99]">{match[1]}</a>;
    }
    if (part.startsWith("`") && part.endsWith("`")) return <code key={i} className="bg-[#0E0E14] px-2 py-1 rounded text-sm font-mono">{part.slice(1, -1)}</code>;
    return part;
  });
};

const parseContent = (content) => {
  const lines = content.split("\n");
  const elements = [];
  let inList = false;
  let listItems = [];

  lines.forEach((line, i) => {
    if (line.startsWith("# ")) {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      elements.push(<h1 key={i} className="text-3xl md:text-4xl font-bold text-white mb-6 mt-8">{line.slice(2)}</h1>);
    } else if (line.startsWith("## ")) {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      elements.push(<h2 key={i} className="text-xl md:text-2xl font-bold text-white mb-4 mt-8 pb-2 border-b border-white/10">{line.slice(3)}</h2>);
    } else if (line.startsWith("### ")) {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      elements.push(<h3 key={i} className="text-lg font-bold text-white mb-3 mt-6">{line.slice(4)}</h3>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      inList = true;
      listItems.push(<li key={i} className="text-[#A1A1AA] mb-2">{formatText(line.slice(2))}</li>);
    } else if (line.match(/^\d+\.\s/)) {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      const text = line.replace(/^\d+\.\s/, "");
      elements.push(<p key={i} className="text-[#A1A1AA] mb-2 pl-4"><span className="text-[#D9F839] font-mono mr-2">{line.match(/^\d+/)[0]}.</span>{formatText(text)}</p>);
    } else if (line === "---") {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      elements.push(<hr key={i} className="border-white/10 my-8" />);
    } else if (line.trim() === "") {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
    } else {
      if (inList) {
        elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4">{listItems}</ul>);
        listItems = [];
        inList = false;
      }
      elements.push(<p key={i} className="text-[#A1A1AA] mb-4 leading-relaxed">{formatText(line)}</p>);
    }
  });

  if (inList) elements.push(<ul key="ul-final" className="list-disc pl-6 mb-4">{listItems}</ul>);
  return elements;
};

const buildJobShareUrl = (job) => {
  const params = new URLSearchParams({ focus: "jobs" });
  if (job.platform) params.set("platform", job.platform);
  if (job.title) params.set("role", job.title);
  return `${window.location.origin}/?${params.toString()}`;
};

const shareJob = async (job) => {
  const url = buildJobShareUrl(job);
  try {
    if (navigator.share) {
      await navigator.share({ title: `${job.title} — ${job.platform}`, url });
      return;
    }
    await navigator.clipboard.writeText(url);
    toast.success("Job link copied — ready to share");
  } catch (error) {
    window.prompt("Copy this job link:", url);
  }
};

const PAGE_SIZE = 50;

const TablePagination = ({ page, totalPages, total, onPage, testId = "pagination" }) => {
  if (totalPages <= 1) return null;
  const windowStart = Math.max(1, Math.min(page - 2, totalPages - 4));
  const pageNumbers = [];
  for (let i = windowStart; i <= Math.min(totalPages, windowStart + 4); i += 1) pageNumbers.push(i);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 border-t border-white/10" data-testid={testId}>
      <span className="text-xs text-[#A1A1AA]" data-testid={`${testId}-summary`}>
        Page {page} of {totalPages} · {total} listings
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-white hover:bg-white/5 disabled:opacity-30 transition-colors"
          data-testid={`${testId}-prev`}
        >
          Prev
        </button>
        {pageNumbers.map((num) => (
          <button
            key={num}
            type="button"
            onClick={() => onPage(num)}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${num === page ? "border-[#D9F839]/60 bg-[#D9F839]/10 text-[#D9F839]" : "border-white/10 text-[#A1A1AA] hover:bg-white/5"}`}
            data-testid={`${testId}-page-${num}`}
          >
            {num}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-white hover:bg-white/5 disabled:opacity-30 transition-colors"
          data-testid={`${testId}-next`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const JobListingsTable = ({ jobs, selectedPlatform, setSelectedPlatform, selectedSection, setSelectedSection, search, setSearch, onReset, title, description, extraPlatforms = [] }) => {
  const [page, setPage] = useState(1);

  const shareCurrentView = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — it opens this exact filtered view");
    } catch (error) {
      window.prompt("Copy this link:", url);
    }
  };

  const platformOptions = useMemo(
    () => [
      "all",
      ...Array.from(new Set([...jobs.map((job) => job.platform), ...extraPlatforms].filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    ],
    [jobs, extraPlatforms]
  );

  const filteredJobs = useMemo(() => {
    const list = jobs.filter((job) => {
      const matchesPlatform = selectedPlatform === "all" || job.platform === selectedPlatform;
      const matchesSection = selectedSection === "all" || job.section === selectedSection;
      const haystack = `${job.title} ${job.platform} ${job.category} ${job.description}`.toLowerCase();
      const matchesSearch = !search || haystack.includes(search.toLowerCase());
      return matchesPlatform && matchesSection && matchesSearch;
    });
    return [...list].sort((a, b) => {
      const aFeat = a.source === "paid-listing" ? 1 : 0;
      const bFeat = b.source === "paid-listing" ? 1 : 0;
      return bFeat - aFeat;
    });
  }, [jobs, search, selectedPlatform, selectedSection]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedPlatform, selectedSection]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleJobs = filteredJobs.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const goToPage = (next) => {
    setPage(Math.min(Math.max(1, next), totalPages));
    document.getElementById("jobs-board")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="jobs-board" className="py-20 px-4 sm:px-6 lg:px-8" data-testid="jobs-board-section">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          title={title || "AI training jobs across expert networks, studies, voice, and micro-tasks"}
          description={description || "Browse curated listings. Use filters to narrow by platform or section."}
          testId="job-board-header"
        />

        <div className="panel-card p-4 md:p-6 mb-6" data-testid="job-board-filters">
          <div className="grid gap-4 lg:grid-cols-[2fr,1fr,1fr,auto] items-end">
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Search roles</label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by role, platform, or domain"
                className="bg-[#070810] border-white/10 text-white"
                data-testid="jobs-search-input"
              />
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Platform</label>
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger className="bg-[#070810] border-white/10 text-white" data-testid="jobs-platform-filter">
                  <SelectValue placeholder="All platforms" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E14] border-white/10 text-white">
                  {platformOptions.map((platform) => (
                    <SelectItem key={platform} value={platform}>{platform === "all" ? "All platforms" : platform}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-[#A1A1AA] block mb-2">Section</label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger className="bg-[#070810] border-white/10 text-white" data-testid="jobs-section-filter">
                  <SelectValue placeholder="All sections" />
                </SelectTrigger>
                <SelectContent className="bg-[#0E0E14] border-white/10 text-white">
                  {SECTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => onReset?.()}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="jobs-reset-filters"
            >
              Reset filters
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA]">
            <span className="stats-chip" data-testid="jobs-total-count">{filteredJobs.length} visible jobs</span>
            <button
              type="button"
              onClick={shareCurrentView}
              className="stats-chip inline-flex items-center gap-2 hover:text-white transition-colors"
              data-testid="jobs-share-view"
            >
              <LinkSimple size={14} /> Copy link to this view
            </button>
          </div>
        </div>

        <div className="panel-card overflow-hidden" data-testid="jobs-table-wrapper">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-[#A1A1AA] min-w-[250px]">Role</TableHead>
                  <TableHead className="text-[#A1A1AA] min-w-[160px]">Platform</TableHead>
                  <TableHead className="text-[#A1A1AA] min-w-[140px]">Focus area</TableHead>
                  <TableHead className="text-[#A1A1AA] min-w-[140px]">Duration</TableHead>
                  <TableHead className="text-[#A1A1AA] min-w-[120px]">Pay</TableHead>
                  <TableHead className="text-[#A1A1AA] min-w-[140px]">Section</TableHead>
                  <TableHead className="text-[#A1A1AA] text-right min-w-[120px]">Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-[#A1A1AA] py-10" data-testid="jobs-empty-state">
                      No jobs match this filter yet. Try adjusting the search or resetting filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleJobs.map((job) => (
                    <TableRow key={job.id} className={`border-white/10 hover:bg-white/5 ${job.source === "paid-listing" ? "bg-[#D9F839]/[0.04]" : ""}`} data-testid={`job-row-${slugify(job.platform)}-${slugify(job.title)}`}>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          {(() => {
                            const iconSrc = resolvePlatformIcon(job);
                            return iconSrc ? (
                              <img src={iconSrc} alt={job.platform} className="w-9 h-9 rounded-md object-contain bg-white/5 p-1 shrink-0" data-testid={`job-icon-${job.id}`} />
                            ) : (
                              <div className="w-9 h-9 rounded-md bg-[#D9F839]/10 text-[#D9F839] flex items-center justify-center text-xs font-mono shrink-0" data-testid={`job-monogram-${job.id}`}>
                                {(job.platform || "?").slice(0, 2).toUpperCase()}
                              </div>
                            );
                          })()}
                          <div className="min-w-0">
                            <div className="text-white font-medium flex items-center gap-2 flex-wrap">
                              <span>{job.title}</span>
                              {job.source === "paid-listing" && (
                                <span className="featured-badge" data-testid={`featured-badge-${job.id}`}>Featured</span>
                              )}
                            </div>
                            {job.description && <div className="text-xs text-[#A1A1AA] mt-1 line-clamp-2">{job.description}</div>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-white">{job.platform}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{job.category || "General"}</TableCell>
                      <TableCell className="text-[#A1A1AA]">{job.duration || "Varies"}</TableCell>
                      <TableCell className="text-[#D9F839] font-medium">{job.pay || "Not listed"}</TableCell>
                      <TableCell><span className="stats-chip">{sectionLabel(job.section)}</span></TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => shareJob(job)}
                            title="Copy shareable link to this role"
                            className="secondary-button inline-flex items-center gap-2 px-3 py-2 rounded-full"
                            data-testid={`job-share-${job.id}`}
                          >
                            <LinkSimple size={14} />
                          </button>
                          <a href={job.link} target="_blank" rel="noopener noreferrer" className="secondary-button inline-flex items-center gap-2 px-4 py-2 rounded-full" data-testid={`job-link-${job.id}`}>
                            Open <ArrowUpRight size={14} />
                          </a>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <TablePagination
            page={currentPage}
            totalPages={totalPages}
            total={filteredJobs.length}
            onPage={goToPage}
            testId="jobs-pagination"
          />
        </div>
      </div>

    </section>
  );
};

const NewsletterSignupForm = ({ source = "web", variant = "default", testId = "newsletter-form" }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      setStatus("success");
      setEmail("");
      toast.success("You're on the list. Watch your inbox on Mondays.");
    } catch (err) {
      setStatus("idle");
      toast.error(err.message || "Something went wrong.");
    }
  };
  const compact = variant === "compact";
  return (
    <form onSubmit={submit} className={compact ? "flex flex-col sm:flex-row gap-2" : "flex flex-col sm:flex-row gap-3"} data-testid={testId}>
      <div className="flex-1 relative">
        <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
        <input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`w-full bg-[#05050A] border border-white/10 rounded-full pl-10 pr-4 ${compact ? "py-2.5 text-sm" : "py-3.5"} text-white focus:outline-none focus:border-[#D9F839]`}
          data-testid={`${testId}-input`}
        />
      </div>
      <button
        type="submit"
        disabled={status === "loading"}
        className={`cta-button rounded-full ${compact ? "px-5 py-2.5 text-sm" : "px-6 py-3.5"} flex items-center justify-center gap-2 disabled:opacity-60`}
        data-testid={`${testId}-submit`}
      >
        {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed" : "Get weekly jobs"}
        {status !== "loading" && <ArrowUpRight size={16} weight="bold" />}
      </button>
    </form>
  );
};



const SiteFooter = () => (
  <footer className="border-t border-white/10 py-12 px-4 sm:px-6 lg:px-8" data-testid="site-footer">
    <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
      <div>
        <div className="text-xl text-white" style={{ fontFamily: "Unbounded, sans-serif" }}>theotautomations.com</div>
        <p className="text-[#A1A1AA] text-sm mt-2 max-w-md">A curated dark job board for AI training, expert networks, one-time studies, voice AI, and micro-task platforms.</p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>Weekly job digest</div>
        <NewsletterSignupForm source="footer" variant="compact" testId="footer-newsletter-form" />
        <div className="text-xs text-[#7A7A85] mt-2">New jobs from expert networks, studies, and micro-tasks — every Monday.</div>
      </div>
      <div className="flex flex-wrap gap-3 text-sm md:justify-end items-start content-start">
        <Link to="/" className="nav-chip" data-testid="footer-home-link">Home</Link>
        <Link to="/?focus=jobs" className="nav-chip" data-testid="footer-jobs-link">Jobs</Link>
        <Link to="/blog" className="nav-chip" data-testid="footer-blog-link">Blog</Link>
        <Link to="/newsletter" className="nav-chip" data-testid="footer-newsletter-link">Newsletter</Link>
        <Link to="/about" className="nav-chip" data-testid="footer-about-link">About Us</Link>
        <Link to="/contact" className="nav-chip" data-testid="footer-contact-link">Contact Us</Link>
        <Link to="/privacy-policy" className="nav-chip" data-testid="footer-privacy-link">Privacy Policy</Link>
        <Link to="/terms" className="nav-chip" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
      </div>
    </div>
    <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-white/5 text-xs text-[#A1A1AA]" data-testid="footer-copyright">
      © 2026 TheOTAutomations.com. All rights reserved.
    </div>
  </footer>
);

const LandingPage = () => {
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [homepage, setHomepage] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPlatform = searchParams.get("platform") || "all";
  const selectedSection = searchParams.get("section") || "all";
  const search = searchParams.get("role") || "";

  const updateFilter = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value || value === "all") next.delete(key);
      else next.set(key, value);
    });
    if (next.get("platform") || next.get("section") || next.get("role")) next.set("focus", "jobs");
    setSearchParams(next, { replace: true });
  };

  const setSelectedPlatform = (value) => updateFilter({ platform: value });
  const setSelectedSection = (value) => updateFilter({ section: value });
  const setSearch = (value) => updateFilter({ role: value });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await bootstrapSite();
        const [postsRes, jobsRes, platformsRes, settingsRes] = await Promise.all([
          axios.get(`${API}/posts`),
          axios.get(`${API}/jobs`),
          axios.get(`${API}/platforms`),
          axios.get(`${API}/settings`),
        ]);
        setPosts(postsRes.data);
        setJobs(jobsRes.data);
        setPlatforms(platformsRes.data);
        setHomepage(settingsRes.data.homepage || {});
      } catch (error) {
        console.error(error);
        toast.error("Failed to load the job board");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const focus = searchParams.get("focus");
    const platform = searchParams.get("platform");
    if (loading || !(focus === "jobs" || platform)) return;
    window.setTimeout(() => {
      document.getElementById("jobs-board")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams.get("focus"), searchParams.get("platform"), searchParams.get("section")]);

  const platformCounts = useMemo(() => jobs.reduce((acc, job) => ({ ...acc, [job.platform]: (acc[job.platform] || 0) + 1 }), {}), [jobs]);
  const bySection = useMemo(() => {
    const groups = { "expert-networks": [], "one-time-studies": [], "voice-ai": [], "micro-tasks": [] };
    platforms.forEach((entry) => { if (groups[entry.section]) groups[entry.section].push(entry); });
    return groups;
  }, [platforms]);
  const copy = (key, fallback) => homepage[key] || fallback;

  const jumpToJobs = (platform, section = "all") => {
    updateFilter({ platform, section, role: "" });
  };

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="landing-page">
      <Header />
      <section className="relative pt-24 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="tech-hero-grid" />
        <div className="tech-radial tech-radial-left" />
        <div className="tech-radial tech-radial-right" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.4fr,0.9fr] gap-10 items-end">
          <div>
            <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block" data-testid="hero-eyebrow">{copy("hero_eyebrow", "AI training jobs, studies, and platforms")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-6 max-w-4xl" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="hero-title">
              {copy("hero_title_lead", "Get paid to")} <span className="text-[#D9F839]">{copy("hero_title_highlight", "train AI")}</span> {copy("hero_title_tail", "— curated remote roles from expert networks, studies, and micro-task platforms.")}
            </h1>
            <p className="text-base md:text-lg text-[#B8B9C3] max-w-2xl mb-8 leading-relaxed" data-testid="hero-description">
              {copy("hero_description", "A dark, always-updating job board featuring expert networks, one-time studies, voice AI platforms, and micro-task work.")}
            </p>
            <div className="flex flex-wrap gap-3">
              <button className="cta-button rounded-full px-7 py-4 flex items-center gap-2" onClick={() => jumpToJobs("all")} data-testid="hero-view-jobs-button">
                Browse open jobs <ArrowUpRight size={18} weight="bold" />
              </button>
              <Link to="/blog" className="secondary-button rounded-full px-7 py-4 flex items-center gap-2" data-testid="hero-blog-button">
                Read the blog <ArrowUpRight size={18} weight="bold" />
              </Link>
            </div>
            <div className="mt-8 max-w-xl" data-testid="hero-newsletter">
              <div className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>Never miss a new listing</div>
              <NewsletterSignupForm source="hero" testId="hero-newsletter-form" />
            </div>
          </div>

          <div className="panel-card p-6 md:p-7" data-testid="hero-stats-panel">
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <div className="text-3xl text-white mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>{jobs.length}</div>
                <div className="text-sm text-[#A1A1AA]">Curated jobs</div>
              </div>
              <div>
                <div className="text-3xl text-white mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>{bySection["expert-networks"].length}</div>
                <div className="text-sm text-[#A1A1AA]">Expert networks</div>
              </div>
              <div>
                <div className="text-3xl text-white mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>{posts.length}</div>
                <div className="text-sm text-[#A1A1AA]">Blog guides</div>
              </div>
            </div>
            <div className="mt-6 text-sm text-[#A1A1AA]" data-testid="hero-stats-note">
              New platforms and studies added regularly — bookmark and check back.
            </div>
          </div>
        </div>
      </section>

      <PlatformMarquee />

      <PlatformSection
        id="expert-networks-section"
        title={copy("expert_title", "Expert networks")}
        description={copy("expert_description", "Top expert AI networks hiring reviewers, trainers, and domain specialists.")}
        entries={bySection["expert-networks"]}
        gridClass="md:grid-cols-2 xl:grid-cols-3"
        cardPrefix="expert-network-card"
        actionPrefix="expert-network-button"
        headerTestId="expert-networks-header"
        platformCounts={platformCounts}
        onJobs={(item) => jumpToJobs(item.platform || item.name, "expert-networks")}
      />

      <PlatformSection
        id="one-time-studies-section"
        title={copy("studies_title", "One-time studies")}
        description={copy("studies_description", "Occasional, well-paid AI research studies from vetted platforms.")}
        entries={bySection["one-time-studies"]}
        gridClass="md:grid-cols-2"
        cardPrefix="study-card"
        actionPrefix="study-visit"
        headerTestId="studies-header"
        platformCounts={platformCounts}
        onJobs={(item) => jumpToJobs(item.platform || item.name, "one-time-studies")}
      />

      <PlatformSection
        id="voice-ai-section"
        title={copy("voice_title", "Voice AI training platforms")}
        description={copy("voice_description", "Voice contribution platforms with clickable signup links.")}
        entries={bySection["voice-ai"]}
        gridClass="md:grid-cols-2"
        cardPrefix="voice-card"
        actionPrefix="voice-visit"
        headerTestId="voice-header"
        platformCounts={platformCounts}
        onJobs={(item) => jumpToJobs(item.platform || item.name, "voice-ai")}
      />

      <PlatformSection
        id="micro-task-section"
        title={copy("micro_title", "Micro-task platforms")}
        description={copy("micro_description", "Short-task platforms for micro-payments, transcription, and data labeling.")}
        entries={bySection["micro-tasks"]}
        gridClass="md:grid-cols-2 xl:grid-cols-4"
        cardPrefix="micro-task-card"
        actionPrefix="micro-task-link"
        headerTestId="micro-task-header"
        platformCounts={platformCounts}
        onJobs={(item) => jumpToJobs(item.platform || item.name, "micro-tasks")}
      />

      {loading ? (
        <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="jobs-loading-state">
          <div className="max-w-7xl mx-auto grid gap-4">
            <div className="skeleton h-28 rounded-3xl" />
            <div className="skeleton h-96 rounded-3xl" />
          </div>
        </section>
      ) : (
        <JobListingsTable
          jobs={jobs}
          selectedPlatform={selectedPlatform}
          setSelectedPlatform={setSelectedPlatform}
          selectedSection={selectedSection}
          setSelectedSection={setSelectedSection}
          search={search}
          setSearch={setSearch}
          onReset={() => updateFilter({ platform: "", section: "", role: "" })}
          title={copy("jobs_title", "")}
          description={copy("jobs_description", "")}
          extraPlatforms={platforms.map((entry) => entry.platform || entry.name)}
        />
      )}

      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            title={copy("blog_title", "Latest guides from theotautomations.com")}
            description={copy("blog_description", "Deep dives, platform breakdowns, and picks to help you earn faster with AI.")}
            testId="blog-preview-header"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.slice(0, 3).map((post, index) => <BlogCard key={post.id} post={post} index={index} featured={index === 0} />)}
          </div>
          <div className="mt-8">
            <Link to="/blog" className="secondary-button inline-flex items-center gap-2 rounded-full px-6 py-3" data-testid="view-all-blog-posts-button">
              View all blog posts <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        await bootstrapSite();
        const response = await axios.get(`${API}/posts`);
        setPosts(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load blog posts");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="blog-index-page">
      <Header />
      <section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="tech-hero-grid" />
        <div className="relative max-w-7xl mx-auto">
          <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block">theotautomations.com</span>
          <h1 className="text-4xl sm:text-5xl text-white mb-5" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="blog-page-title">Guides and breakdowns to help you earn with AI</h1>
          <p className="text-base md:text-lg text-[#B8B9C3] max-w-2xl mb-8" data-testid="blog-page-description">Deep-dive tutorials and platform reviews that pair with the job board.</p>
          <Link to="/?focus=jobs" className="cta-button inline-flex items-center gap-2 rounded-full px-6 py-3" data-testid="back-to-job-board-button">
            Back to job board <ArrowUpRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      <PlatformMarquee />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((item) => <div key={item} className="skeleton rounded-3xl h-80" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-post-grid">
              {posts.map((post, index) => <BlogCard key={post.id} post={post} index={index} featured={index === 0} />)}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/posts/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error(error);
        toast.error("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A]">
        <Header />
        <div className="pt-24 px-4 max-w-4xl mx-auto">
          <div className="skeleton h-12 w-3/4 mb-4 rounded" />
          <div className="skeleton h-64 w-full mb-8 rounded" />
          <div className="skeleton h-4 w-full mb-2 rounded" />
          <div className="skeleton h-4 w-5/6 mb-2 rounded" />
          <div className="skeleton h-4 w-4/5 rounded" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#05050A]">
        <Header />
        <div className="pt-24 px-4 text-center" data-testid="post-not-found-state">
          <h1 className="text-2xl text-white">Post not found</h1>
          <Link to="/blog" className="text-[#D9F839] mt-4 inline-block" data-testid="post-not-found-back-link">Go back to the blog</Link>
        </div>
      </div>
    );
  }

  const currentUrl = window.location.href;

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="blog-post-page">
      <Header />
      <div className="relative aspect-[16/7] mt-16 min-h-[220px]">
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent" />
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-28 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="panel-card p-6 md:p-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-6" data-testid="back-to-blog">
            <ArrowLeft size={16} /> Back to blog
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            <span className="tag px-2 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full">{post.category}</span>
            {post.tags.slice(0, 3).map((tag) => <span key={tag} className="tag px-2 py-1 bg-white/5 text-[#A1A1AA] rounded-full">{tag}</span>)}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="post-title">{post.title}</h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-[#A1A1AA] mb-6 pb-6 border-b border-white/10">
            <span className="flex items-center gap-2"><User size={16} weight="fill" className="text-[#D9F839]" />{post.author || "Anonymous AI Guru"}</span>
            <span className="flex items-center gap-2"><CalendarBlank size={16} weight="fill" className="text-[#D9F839]" />{formatDate(post.created_at)}</span>
          </div>

          <div className="mb-8"><SocialShare title={post.title} url={currentUrl} /></div>
          {post.video_url && <VideoEmbed url={post.video_url} />}

          {post.referral_links && post.referral_links.length > 0 && (
            <div className="mb-8 p-6 bg-[#0E0E14] border border-white/10 rounded-2xl" data-testid="post-referrals-top">
              <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }}>Quick Links - Start Earning Now</h3>
              {post.referral_links.map((link) => <ReferralButton key={link.name} link={link} />)}
            </div>
          )}

          <div className="prose max-w-none" data-testid="post-content">{parseContent(post.content)}</div>

          {post.slug === "50-ai-training-sites" && <DirectoryList />}

          {post.referral_links && post.referral_links.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10" data-testid="post-referrals-bottom">
              <h3 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: "Unbounded, sans-serif" }}>Ready to Start?</h3>
              {post.referral_links.map((link) => <ReferralButton key={`bottom-${link.name}`} link={link} />)}
            </div>
          )}

          <div className="mt-12 pt-8 border-t border-white/10"><SocialShare title={post.title} url={currentUrl} /></div>
        </motion.div>
      </article>
    </div>
  );
};

const PostAJobPage = () => {
  const [packages, setPackages] = useState([]);
  const [selectedId, setSelectedId] = useState("featured_7day");
  const [submitting, setSubmitting] = useState(false);
  const [creditInfo, setCreditInfo] = useState(null);
  const [creditError, setCreditError] = useState("");
  const [form, setForm] = useState({
    company_name: "",
    contact_email: "",
    job_title: "",
    job_link: "",
    section: "expert-networks",
    platform: "",
    description: "",
    icon_url: "",
  });

  const params = new URLSearchParams(window.location.search);
  const creditToken = params.get("token") || "";

  useEffect(() => {
    fetch(`${API}/payments/packages`).then((r) => r.json()).then(setPackages).catch(() => {});
  }, []);

  useEffect(() => {
    if (!creditToken) return;
    fetch(`${API}/payments/credit/${creditToken}`)
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.detail || "Invalid or expired token");
        }
        return r.json();
      })
      .then((info) => {
        setCreditInfo(info);
        setForm((f) => ({ ...f, contact_email: info.contact_email || f.contact_email }));
      })
      .catch((e) => setCreditError(e.message));
  }, [creditToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_email || !form.job_title || !form.job_link) {
      toast.error("Fill company, email, job title and link.");
      return;
    }
    setSubmitting(true);
    try {
      if (creditInfo && creditInfo.credits_remaining > 0) {
        const res = await fetch(`${API}/payments/redeem-credit`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credit_token: creditToken, ...form }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed to post");
        toast.success(`Listing added. ${data.credits_remaining} credit${data.credits_remaining === 1 ? "" : "s"} left.`);
        setCreditInfo({ ...creditInfo, credits_remaining: data.credits_remaining });
        setForm({ ...form, job_title: "", job_link: "", description: "", icon_url: "" });
        setSubmitting(false);
        return;
      }
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: selectedId, origin_url: window.location.origin, ...form }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Checkout failed");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.message);
      setSubmitting(false);
    }
  };

  const inCreditMode = !!creditInfo && creditInfo.credits_remaining > 0;

  return (
    <div className="bg-[#05050A] text-white min-h-screen" data-testid="post-a-job-page">
      <Header />
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block">For companies</span>
          <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="post-a-job-title">
            {inCreditMode ? "Redeem your listing credits" : "Post a featured job on the board"}
          </h1>
          <p className="text-base md:text-lg text-[#B8B9C3] max-w-2xl mb-10">
            {inCreditMode
              ? `Post another job from your ${creditInfo.package_name}. ${creditInfo.credits_remaining} of ${creditInfo.credits_total} credits remaining.`
              : "Pick a package, share your listing details, and pay securely with card. Your job is added to the board the moment payment is confirmed."}
          </p>

          {creditToken && creditError && (
            <div className="panel-card p-4 mb-6 border border-red-500/30 text-red-300" data-testid="credit-error">{creditError}</div>
          )}

          {!inCreditMode && (
            <>
              <div className="grid gap-4 md:grid-cols-3 mb-6" data-testid="pricing-grid">
                {packages.filter((p) => p.credits === 1).map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => setSelectedId(pkg.id)}
                    className={`panel-card p-5 text-left transition-all border ${selectedId === pkg.id ? "border-[#D9F839]" : "border-white/10"}`}
                    data-testid={`package-${pkg.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-lg" style={{ fontFamily: "Unbounded, sans-serif" }}>{pkg.name}</span>
                      {selectedId === pkg.id && <CheckCircle size={20} weight="fill" className="text-[#D9F839]" />}
                    </div>
                    <div className="text-3xl text-[#D9F839] mb-2" style={{ fontFamily: "Unbounded, sans-serif" }}>${pkg.amount}</div>
                    <div className="text-sm text-[#A1A1AA]">{pkg.description}</div>
                  </button>
                ))}
              </div>

              <div className="mb-10" data-testid="bundle-section">
                <div className="text-sm uppercase tracking-widest text-[#A1A1AA] mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>
                  Hiring for multiple roles? Save with a pack
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {packages.filter((p) => p.credits > 1).map((pkg) => (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => setSelectedId(pkg.id)}
                      className={`panel-card p-5 text-left transition-all border relative ${selectedId === pkg.id ? "border-[#D9F839]" : "border-white/10"}`}
                      data-testid={`package-${pkg.id}`}
                    >
                      <span className="absolute -top-2 -right-2 bg-[#D9F839] text-black text-xs px-2 py-0.5 rounded-full font-bold">{pkg.credits} jobs</span>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white text-base" style={{ fontFamily: "Unbounded, sans-serif" }}>{pkg.name}</span>
                        {selectedId === pkg.id && <CheckCircle size={20} weight="fill" className="text-[#D9F839]" />}
                      </div>
                      <div className="text-2xl text-[#D9F839] mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>${pkg.amount}</div>
                      <div className="text-xs text-[#A1A1AA] mb-2">${(pkg.amount / pkg.credits).toFixed(2)} per listing</div>
                      <div className="text-xs text-[#A1A1AA]">{pkg.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <form onSubmit={submit} className="panel-card p-6 grid gap-4 md:grid-cols-2" data-testid="post-a-job-form">
            <div className="md:col-span-1">
              <label className="text-[#A1A1AA] text-sm block mb-2">Company name *</label>
              <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="company-name-input" />
            </div>
            <div className="md:col-span-1">
              <label className="text-[#A1A1AA] text-sm block mb-2">Contact email *</label>
              <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="contact-email-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[#A1A1AA] text-sm block mb-2">Job title *</label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="job-title-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[#A1A1AA] text-sm block mb-2">Apply link *</label>
              <Input value={form.job_link} onChange={(e) => setForm({ ...form, job_link: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="https://..." required data-testid="job-link-input-post" />
            </div>
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Section</label>
              <select value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="section-select">
                <option value="expert-networks">Expert networks</option>
                <option value="one-time-studies">One-time studies</option>
                <option value="voice-ai">Voice AI</option>
                <option value="micro-tasks">Micro-tasks</option>
              </select>
            </div>
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Platform label (optional)</label>
              <Input value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="Defaults to company name" data-testid="platform-input" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[#A1A1AA] text-sm block mb-2">Company logo URL (optional)</label>
              <Input value={form.icon_url} onChange={(e) => setForm({ ...form, icon_url: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="https://.../logo.png" data-testid="icon-url-input" />
              <div className="text-xs text-[#A1A1AA] mt-1">Square PNG/JPG works best. Leave blank to use a monogram.</div>
            </div>
            <div className="md:col-span-2">
              <label className="text-[#A1A1AA] text-sm block mb-2">Short description</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full min-h-[110px] bg-[#05050A] border border-white/10 rounded-md p-3 text-white" data-testid="description-input" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
              <div className="text-sm text-[#A1A1AA]">
                {inCreditMode
                  ? <>Using <span className="text-white">{creditInfo.package_name}</span> · {creditInfo.credits_remaining} credit{creditInfo.credits_remaining === 1 ? "" : "s"} left</>
                  : <>Selected: <span className="text-white">{packages.find((p) => p.id === selectedId)?.name || "—"}</span></>}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="cta-button rounded-full px-7 py-4 flex items-center gap-2 disabled:opacity-60"
                data-testid="checkout-button"
              >
                {inCreditMode ? <><Rocket size={18} weight="bold" /> {submitting ? "Posting..." : "Post this job"}</> : <><CreditCard size={18} weight="bold" /> {submitting ? "Redirecting..." : "Pay & post job"}</>}
              </button>
            </div>
          </form>

          {!inCreditMode && (
            <div className="mt-6 text-xs text-[#A1A1AA]" data-testid="test-card-hint">
              Testing? Use card <span className="text-white">4242 4242 4242 4242</span>, any future date, any CVC.
            </div>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

const PaymentSuccessPage = () => {
  const [status, setStatus] = useState({ payment_status: "pending", listing_created: false });
  const [attempts, setAttempts] = useState(0);
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (cancelled) return;
        setStatus(data);
        if (data.payment_status !== "paid" && attempts < 15) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch {
        if (!cancelled && attempts < 15) setTimeout(() => setAttempts((a) => a + 1), 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [attempts, sessionId]);

  const paid = status.payment_status === "paid";
  const hasBundle = paid && (status.credits_total || 1) > 1;
  const remaining = status.credits_remaining || 0;
  const claimUrl = status.credit_token ? `${window.location.origin}/post-a-job?token=${status.credit_token}` : "";
  return (
    <div className="bg-[#05050A] text-white min-h-screen" data-testid="payment-success-page">
      <Header />
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center panel-card p-10">
          {paid ? (
            <>
              <CheckCircle size={64} weight="fill" className="text-[#D9F839] mx-auto mb-6" />
              <h1 className="text-3xl md:text-4xl text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="payment-success-title">Payment received</h1>
              <p className="text-[#A1A1AA] mb-6">
                {hasBundle ? `Your first listing is live. You have ${remaining} more credit${remaining === 1 ? "" : "s"} to use.` : "Your featured listing is live on the job board."}
              </p>
              {hasBundle && claimUrl && (
                <div className="panel-card p-4 mb-6 text-left" data-testid="claim-panel">
                  <div className="text-xs uppercase tracking-widest text-[#A1A1AA] mb-2">Post more with your remaining credits</div>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={claimUrl} className="bg-[#05050A] border-white/10 text-xs" data-testid="claim-url-input" />
                    <button
                      type="button"
                      onClick={() => { navigator.clipboard.writeText(claimUrl); toast.success("Link copied"); }}
                      className="secondary-button rounded-full px-4 py-2 text-xs"
                      data-testid="copy-claim-url-button"
                    >
                      Copy
                    </button>
                  </div>
                  <div className="text-xs text-[#A1A1AA] mt-2">Bookmark this link — anyone with it can post the remaining jobs against your pack.</div>
                </div>
              )}
              <div className="flex justify-center gap-3 flex-wrap">
                <Link to="/?focus=jobs" className="cta-button rounded-full px-6 py-3 flex items-center gap-2" data-testid="go-to-board-button"><Rocket size={16} weight="bold" /> Visit jobs board</Link>
                {hasBundle && remaining > 0 ? (
                  <Link to={`/post-a-job?token=${status.credit_token}`} className="secondary-button rounded-full px-6 py-3" data-testid="post-another-button">Post next job</Link>
                ) : (
                  <Link to="/post-a-job" className="secondary-button rounded-full px-6 py-3" data-testid="post-another-button">Post another</Link>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full border-4 border-[#D9F839]/30 border-t-[#D9F839] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="payment-pending-title">Confirming payment...</h1>
              <p className="text-[#A1A1AA]">This usually takes a few seconds.</p>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

const PaymentCancelPage = () => (
  <div className="bg-[#05050A] text-white min-h-screen" data-testid="payment-cancel-page">
    <Header />
    <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto text-center panel-card p-10">
        <XCircle size={64} weight="fill" className="text-[#A1A1AA] mx-auto mb-6" />
        <h1 className="text-3xl md:text-4xl text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="payment-cancel-title">Payment cancelled</h1>
        <p className="text-[#A1A1AA] mb-8">No charge was made. You can pick a package and try again.</p>
        <Link to="/post-a-job" className="cta-button rounded-full px-6 py-3 inline-flex items-center gap-2" data-testid="try-again-button">Try again</Link>
      </div>
    </section>
    <SiteFooter />
  </div>
);


const NewsletterPage = () => (
  <div className="bg-[#05050A] text-white min-h-screen" data-testid="newsletter-page">
    <Header />
    <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center">
        <span className="tag px-3 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full mb-5 inline-block">Weekly digest</span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-5" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="newsletter-page-title">
          New AI training jobs every Monday
        </h1>
        <p className="text-base md:text-lg text-[#B8B9C3] mb-10 max-w-2xl mx-auto">
          One short email a week with the freshest listings from expert networks, one-time studies, voice AI platforms, and micro-task platforms. Unsubscribe any time.
        </p>
        <div className="max-w-xl mx-auto">
          <NewsletterSignupForm source="newsletter-page" testId="newsletter-page-form" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-14 text-left">
          <div className="panel-card p-5"><div className="text-[#D9F839] text-sm mb-1">Curated</div><div className="text-white">Only hand-picked platforms with legitimate paid AI work.</div></div>
          <div className="panel-card p-5"><div className="text-[#D9F839] text-sm mb-1">Concise</div><div className="text-white">One email, 5–10 listings, ~2 minute read.</div></div>
          <div className="panel-card p-5"><div className="text-[#D9F839] text-sm mb-1">No spam</div><div className="text-white">One-click unsubscribe in every email.</div></div>
        </div>
      </div>
    </section>
    <SiteFooter />
  </div>
);

const NewsletterUnsubscribePage = () => {
  const [status, setStatus] = useState("loading");
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  useEffect(() => {
    if (!token) { setStatus("error"); return; }
    fetch(`${API}/newsletter/unsubscribe/${token}`).then(async (r) => {
      if (!r.ok) throw new Error();
      setStatus("done");
    }).catch(() => setStatus("error"));
  }, [token]);
  return (
    <div className="bg-[#05050A] text-white min-h-screen" data-testid="unsubscribe-page">
      <Header />
      <section className="pt-32 pb-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center panel-card p-10">
          {status === "loading" && <div className="text-[#A1A1AA]">Unsubscribing...</div>}
          {status === "done" && (
            <>
              <CheckCircle size={64} weight="fill" className="text-[#D9F839] mx-auto mb-6" />
              <h1 className="text-3xl text-white mb-3" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="unsubscribe-success-title">You&apos;re unsubscribed</h1>
              <p className="text-[#A1A1AA]">Sorry to see you go. You can always resubscribe from the homepage.</p>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle size={64} weight="fill" className="text-[#A1A1AA] mx-auto mb-6" />
              <h1 className="text-2xl text-white mb-3" data-testid="unsubscribe-error-title">This link is no longer valid</h1>
              <p className="text-[#A1A1AA]">The token may have expired or already been used.</p>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};


const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("admin_token")) navigate("/admin/dashboard", { replace: true });
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await bootstrapSite();
      const response = await axios.post(`${API}/admin/login`, { username, password });
      login(response.data.access_token);
      toast.success("Logged in successfully");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error(error);
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="panel-card p-8">
          <div className="platform-icon-shell mb-5" data-testid="admin-login-badge">AD</div>
          <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "Unbounded, sans-serif" }}>Admin Login</h1>
          <p className="text-[#A1A1AA] text-sm mb-6">Manage jobs, blog posts, categories, and site settings.</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Username</label>
              <Input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="bg-[#05050A] border-white/10 text-white" placeholder="Enter username" data-testid="admin-username-input" />
            </div>
            <div>
              <label className="text-[#A1A1AA] text-sm block mb-2">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#05050A] border-white/10 text-white" placeholder="Enter password" data-testid="admin-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full cta-button rounded-full" data-testid="admin-login-button">{loading ? "Logging in..." : "LOGIN"}</Button>
          </form>
          <Link to="/" className="text-[#D9F839] text-sm block text-center mt-6" data-testid="admin-back-to-site">← Back to site</Link>
        </div>
      </motion.div>
    </div>
  );
};

const AdminDashboard = () => {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [payments, setPayments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignForm, setCampaignForm] = useState({ subject: "", body_html: "" });
  const [testEmail, setTestEmail] = useState("");
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ title: "", slug: "", excerpt: "", content: "", cover_image: "", category: "", tags: "", author: "Anonymous AI Guru", video_url: "", published: true, referral_links: [] });
  const [jobForm, setJobForm] = useState({ title: "", platform: "", section: "expert-networks", category: "", duration: "", pay: "", location: "Remote / varies", description: "", link: "", link_type: "referral", source: "manual", company: "", icon_url: "", source_order: 0, active: true });
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkDefaultSection, setBulkDefaultSection] = useState("expert-networks");
  const [newReferralLink, setNewReferralLink] = useState({ name: "", url: "", bonus: "", description: "" });
  const [categoryForm, setCategoryForm] = useState({ name: "", slug: "", description: "" });
  const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [settingsForm, setSettingsForm] = useState({ site_name: "", tagline: "", menu_items: [] });
  const [newMenuItem, setNewMenuItem] = useState({ label: "", url: "", order: 1 });
  const [jobSearch, setJobSearch] = useState("");
  const [jobPage, setJobPage] = useState(1);
  const [platformEntries, setPlatformEntries] = useState([]);
  const [homepageForm, setHomepageForm] = useState({});
  const [savingHomepage, setSavingHomepage] = useState(false);
  const [isPlatformDialogOpen, setIsPlatformDialogOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [platformForm, setPlatformForm] = useState(EMPTY_PLATFORM_FORM);
  const [directorySites, setDirectorySites] = useState([]);
  const [directorySearch, setDirectorySearch] = useState("");
  const [isDirectoryDialogOpen, setIsDirectoryDialogOpen] = useState(false);
  const [editingDirectorySite, setEditingDirectorySite] = useState(null);
  const [directoryForm, setDirectoryForm] = useState(EMPTY_DIRECTORY_FORM);

  const filteredAdminJobs = useMemo(() => {
    if (!jobSearch.trim()) return jobs;
    const q = jobSearch.trim().toLowerCase();
    return jobs.filter((job) => `${job.title} ${job.platform} ${job.category} ${job.source}`.toLowerCase().includes(q));
  }, [jobs, jobSearch]);
  const adminTotalPages = Math.max(1, Math.ceil(filteredAdminJobs.length / PAGE_SIZE));
  const adminJobPage = Math.min(jobPage, adminTotalPages);
  const visibleAdminJobs = filteredAdminJobs.slice((adminJobPage - 1) * PAGE_SIZE, adminJobPage * PAGE_SIZE);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchAll();
  }, [token, navigate]);

  const fetchAll = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [postsRes, jobsRes, catsRes, settingsRes, paymentsRes, subsRes, campsRes, platformsRes, directoryRes] = await Promise.all([
        axios.get(`${API}/posts?published_only=false`, { headers }),
        axios.get(`${API}/admin/jobs`, { headers }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/payments`, { headers }),
        axios.get(`${API}/admin/subscribers`, { headers }),
        axios.get(`${API}/admin/campaigns`, { headers }),
        axios.get(`${API}/admin/platforms`, { headers }),
        axios.get(`${API}/admin/directory`, { headers }),
      ]);
      setPosts(postsRes.data);
      setJobs(jobsRes.data);
      setCategories(catsRes.data);
      setSettings(settingsRes.data);
      setPayments(paymentsRes.data);
      setSubscribers(subsRes.data);
      setCampaigns(campsRes.data);
      setPlatformEntries(platformsRes.data);
      setDirectorySites(directoryRes.data);
      setHomepageForm(settingsRes.data.homepage || {});
      setSettingsForm({ site_name: settingsRes.data.site_name || "", tagline: settingsRes.data.tagline || "", menu_items: settingsRes.data.menu_items || [] });
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        logout();
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPost(null);
    setFormData({ title: "", slug: "", excerpt: "", content: "", cover_image: "", category: categories[0]?.name || "", tags: "", author: "Anonymous AI Guru", video_url: "", published: true, referral_links: [] });
    setNewReferralLink({ name: "", url: "", bonus: "", description: "" });
    setIsDialogOpen(true);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({ title: post.title, slug: post.slug, excerpt: post.excerpt, content: post.content, cover_image: post.cover_image, category: post.category, tags: post.tags.join(", "), author: post.author || "Anonymous AI Guru", video_url: post.video_url || "", published: post.published, referral_links: post.referral_links || [] });
    setNewReferralLink({ name: "", url: "", bonus: "", description: "" });
    setIsDialogOpen(true);
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setJobForm({ title: "", platform: "", section: "expert-networks", category: "", duration: "", pay: "", location: "Remote / varies", description: "", link: "", link_type: "referral", source: "manual", company: "", icon_url: "", source_order: 0, active: true });
    setIsJobDialogOpen(true);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobForm({ title: job.title, platform: job.platform, section: job.section, category: job.category || "", duration: job.duration || "", pay: job.pay || "", location: job.location || "Remote / varies", description: job.description || "", link: job.link, link_type: job.link_type || "direct", source: job.source || "manual", company: job.company || "", icon_url: job.icon_url || "", source_order: job.source_order || 0, active: job.active });
    setIsJobDialogOpen(true);
  };

  const addReferralLink = () => {
    if (!newReferralLink.name || !newReferralLink.url) {
      toast.error("Name and URL are required");
      return;
    }
    setFormData((prev) => ({ ...prev, referral_links: [...prev.referral_links, { ...newReferralLink }] }));
    setNewReferralLink({ name: "", url: "", bonus: "", description: "" });
  };

  const removeReferralLink = (index) => {
    setFormData((prev) => ({ ...prev, referral_links: prev.referral_links.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const data = { ...formData, tags: formData.tags.split(",").map((tag) => tag.trim()).filter(Boolean), video_url: formData.video_url || null };
      if (editingPost) {
        await axios.put(`${API}/posts/${editingPost.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/posts`, data, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Post created");
      }
      setIsDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error saving post");
    }
  };

  const handleJobSubmit = async (event) => {
    event.preventDefault();
    try {
      if (editingJob) {
        await axios.put(`${API}/jobs/${editingJob.id}`, jobForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job updated");
      } else {
        await axios.post(`${API}/jobs`, jobForm, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job created");
      }
      setIsJobDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error saving job");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Post deleted");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error deleting post");
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm("Delete this job listing?")) return;
    try {
      await axios.delete(`${API}/jobs/${jobId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Job deleted");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error deleting job");
    }
  };

  const openNewPlatform = (section) => {
    setEditingPlatform(null);
    const inSection = platformEntries.filter((entry) => entry.section === section);
    setPlatformForm({
      ...EMPTY_PLATFORM_FORM,
      section,
      action: section === "expert-networks" ? "jobs" : "link",
      order: inSection.length + 1,
    });
    setIsPlatformDialogOpen(true);
  };

  const openEditPlatform = (entry) => {
    setEditingPlatform(entry);
    setPlatformForm({ ...EMPTY_PLATFORM_FORM, ...entry });
    setIsPlatformDialogOpen(true);
  };

  const handlePlatformSubmit = async (event) => {
    event.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };
    const payload = {
      ...platformForm,
      platform: platformForm.platform || platformForm.name,
      badge: platformForm.badge || platformForm.name.slice(0, 2).toUpperCase(),
      order: Number(platformForm.order) || 0,
    };
    try {
      if (editingPlatform) {
        await axios.put(`${API}/admin/platforms/${editingPlatform.id}`, payload, { headers });
        toast.success("Platform updated");
      } else {
        await axios.post(`${API}/admin/platforms`, payload, { headers });
        toast.success("Platform added to the homepage");
      }
      setIsPlatformDialogOpen(false);
      setEditingPlatform(null);
      setPlatformForm(EMPTY_PLATFORM_FORM);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error saving platform");
    }
  };

  const handleDeletePlatform = async (entryId) => {
    if (!window.confirm("Remove this platform from the homepage?")) return;
    try {
      await axios.delete(`${API}/admin/platforms/${entryId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Platform removed");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error removing platform");
    }
  };

  const filteredDirectory = useMemo(() => {
    if (!directorySearch.trim()) return directorySites;
    const q = directorySearch.trim().toLowerCase();
    return directorySites.filter((site) => `${site.name} ${site.category} ${site.url}`.toLowerCase().includes(q));
  }, [directorySites, directorySearch]);

  const openNewDirectorySite = () => {
    setEditingDirectorySite(null);
    setDirectoryForm({ ...EMPTY_DIRECTORY_FORM, order: directorySites.length + 1 });
    setIsDirectoryDialogOpen(true);
  };

  const openEditDirectorySite = (site) => {
    setEditingDirectorySite(site);
    setDirectoryForm({ ...EMPTY_DIRECTORY_FORM, ...site });
    setIsDirectoryDialogOpen(true);
  };

  const handleDirectorySubmit = async (event) => {
    event.preventDefault();
    const headers = { Authorization: `Bearer ${token}` };
    const payload = { ...directoryForm, order: Number(directoryForm.order) || 0 };
    try {
      if (editingDirectorySite) {
        await axios.put(`${API}/admin/directory/${editingDirectorySite.id}`, payload, { headers });
        toast.success("Directory site updated");
      } else {
        await axios.post(`${API}/admin/directory`, payload, { headers });
        toast.success("Directory site added");
      }
      setIsDirectoryDialogOpen(false);
      setEditingDirectorySite(null);
      setDirectoryForm(EMPTY_DIRECTORY_FORM);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error saving directory site");
    }
  };

  const handleDeleteDirectorySite = async (siteId) => {
    if (!window.confirm("Remove this site from the directory article?")) return;
    try {
      await axios.delete(`${API}/admin/directory/${siteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Directory site removed");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error removing directory site");
    }
  };

  const handleSaveHomepage = async (event) => {
    event.preventDefault();
    setSavingHomepage(true);
    try {
      await axios.put(`${API}/settings`, { homepage: homepageForm }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Homepage content saved");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error saving homepage content");
    } finally {
      setSavingHomepage(false);
    }
  };


  const handleCreateCategory = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API}/categories`, categoryForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Category created");
      setCategoryForm({ name: "", slug: "", description: "" });
      setIsCategoryDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error creating category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Category deleted");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error deleting category");
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    try {
      await axios.post(`${API}/admin/change-password`, { current_password: passwordForm.current_password, new_password: passwordForm.new_password }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
      setIsPasswordDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Error changing password");
    }
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();
    try {
      await axios.put(`${API}/settings`, settingsForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Settings saved");
      setIsSettingsDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Error saving settings");
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    if (!campaignForm.subject || !campaignForm.body_html) { toast.error("Subject and body required"); return; }
    try {
      await axios.post(`${API}/admin/campaigns`, campaignForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Campaign draft saved");
      setCampaignForm({ subject: "", body_html: "" });
      fetchAll();
    } catch (err) { toast.error("Failed to save campaign"); console.error(err); }
  };

  const handleSendTest = async (campaignId) => {
    if (!testEmail) { toast.error("Enter a test email"); return; }
    try {
      const res = await axios.post(`${API}/admin/campaigns/${campaignId}/send-test`, { email: testEmail }, { headers: { Authorization: `Bearer ${token}` } });
      toast[res.data.status === "ok" ? "success" : "error"](res.data.status === "ok" ? "Test email sent" : "Test failed");
    } catch (err) { toast.error("Test failed"); console.error(err); }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm("Send this campaign to all active subscribers?")) return;
    try {
      const res = await axios.post(`${API}/admin/campaigns/${campaignId}/send`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent to ${res.data.sent}, failed ${res.data.failed}`);
      fetchAll();
    } catch (err) { toast.error("Send failed"); console.error(err); }
  };

  const handleSendWeeklyDigest = async () => {
    if (!window.confirm("Generate & send this week's digest now to all active subscribers?")) return;
    setSendingWeekly(true);
    try {
      const res = await axios.post(`${API}/admin/newsletter/send-weekly-digest`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Weekly digest sent to ${res.data.sent}, failed ${res.data.failed}`);
      fetchAll();
    } catch (err) { toast.error("Failed to send weekly digest"); console.error(err); }
    setSendingWeekly(false);
  };

  const handleUnsubscribeSubscriber = async (sub) => {
    if (!window.confirm(`Remove ${sub.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${sub.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Subscriber removed");
      fetchAll();
    } catch (err) { toast.error("Failed"); console.error(err); }
  };



  const addMenuItem = () => {
    if (!newMenuItem.label || !newMenuItem.url) return;
    setSettingsForm((prev) => ({ ...prev, menu_items: [...prev.menu_items, { ...newMenuItem, order: prev.menu_items.length + 1 }] }));
    setNewMenuItem({ label: "", url: "", order: 1 });
  };

  const removeMenuItem = (index) => {
    setSettingsForm((prev) => ({ ...prev, menu_items: prev.menu_items.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const handleReimportJobs = async () => {
    try {
      await axios.post(`${API}/jobs/seed-sample`);
      toast.success("Sample jobs synced");
      fetchAll();
    } catch (error) {
      console.error(error);
      toast.error("Failed to sync sample jobs");
    }
  };

  const handleBulkUpload = async (e) => {
    e.preventDefault();
    const fileInput = e.target.elements.file;
    const f = fileInput.files[0];
    if (!f) { toast.error("Choose a CSV or XLSX file first"); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("default_section", bulkDefaultSection);
      const res = await axios.post(`${API}/admin/jobs/bulk-upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setBulkResult(res.data);
      toast.success(`Uploaded — ${res.data.inserted} added, ${res.data.skipped_duplicates} duplicates skipped`);
      fetchAll();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    }
    setBulkUploading(false);
  };

  const downloadTemplateCsv = () => {
    const headers = ["Job Title","Platform","Section","Domain","Duration","Pay","Location","Description","Link","Link Type","Company","Icon URL"];
    const example = ["AI Trainer","AcmeAI","expert-networks","Coding","Flexible","$40/hr","Remote","Review coding tasks.","https://acmeai.example/apply","direct","AcmeAI","https://.../logo.png"];
    const csv = headers.join(",") + "\n" + example.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "job-listings-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };



  const handleLogout = () => {
    logout();
    navigate("/admin");
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-[#05050A] p-4 md:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Unbounded, sans-serif" }}>Admin Dashboard</h1>
            <p className="text-[#A1A1AA] text-sm">Manage job listings, blog posts, categories, and site settings.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/"><Button variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="admin-view-site">View Site</Button></Link>
            <Button onClick={handleLogout} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="admin-logout"><SignOut size={18} className="mr-2" /> Logout</Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4 mb-8" data-testid="admin-summary-cards">
          <div className="panel-card p-5"><div className="text-sm text-[#A1A1AA] mb-1">Jobs</div><div className="text-3xl text-white">{jobs.length}</div></div>
          <div className="panel-card p-5"><div className="text-sm text-[#A1A1AA] mb-1">Posts</div><div className="text-3xl text-white">{posts.length}</div></div>
          <div className="panel-card p-5"><div className="text-sm text-[#A1A1AA] mb-1">Categories</div><div className="text-3xl text-white">{categories.length}</div></div>
          <div className="panel-card p-5"><div className="text-sm text-[#A1A1AA] mb-1">Site name</div><div className="text-lg text-white">{settings?.site_name || "theotautomations.com"}</div></div>
        </div>

        <Tabs defaultValue="jobs" className="space-y-6">
          <TabsList className="bg-[#0E0E14] border border-white/10 flex flex-wrap h-auto">
            <TabsTrigger value="jobs" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-jobs"><ListBullets size={16} className="mr-2" /> Jobs</TabsTrigger>
            <TabsTrigger value="posts" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-posts"><ListBullets size={16} className="mr-2" /> Blog Posts</TabsTrigger>
            <TabsTrigger value="newsletter" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-newsletter"><EnvelopeSimple size={16} className="mr-2" /> Newsletter</TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-payments"><CreditCard size={16} className="mr-2" /> Payments</TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-categories"><Tag size={16} className="mr-2" /> Categories</TabsTrigger>
            <TabsTrigger value="homepage" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-homepage"><Gear size={16} className="mr-2" /> Homepage</TabsTrigger>
            <TabsTrigger value="directory" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-directory"><ListNumbers size={16} className="mr-2" /> Directory</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#D9F839] data-[state=active]:text-black" data-testid="admin-tab-settings"><Gear size={16} className="mr-2" /> Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs">
            <div className="flex flex-wrap gap-3 mb-6">
              <Dialog open={isJobDialogOpen} onOpenChange={setIsJobDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={handleCreateJob} className="cta-button rounded-full" data-testid="create-job-button"><Plus size={18} className="mr-2" /> New Job</Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>{editingJob ? "Edit Job" : "Create New Job"}</DialogTitle></DialogHeader>
                  <form onSubmit={handleJobSubmit} className="space-y-4 mt-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Title</label><Input value={jobForm.title} onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="job-title-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Platform</label><Input value={jobForm.platform} onChange={(e) => setJobForm({ ...jobForm, platform: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="job-platform-input" /></div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Section</label><select value={jobForm.section} onChange={(e) => setJobForm({ ...jobForm, section: e.target.value })} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="job-section-input">{SECTION_OPTIONS.filter((option) => option.value !== "all").map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Category / Domain</label><Input value={jobForm.category} onChange={(e) => setJobForm({ ...jobForm, category: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="job-category-input" /></div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Duration</label><Input value={jobForm.duration} onChange={(e) => setJobForm({ ...jobForm, duration: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="job-duration-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Pay</label><Input value={jobForm.pay} onChange={(e) => setJobForm({ ...jobForm, pay: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="job-pay-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Location</label><Input value={jobForm.location} onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="job-location-input" /></div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Company</label><Input value={jobForm.company} onChange={(e) => setJobForm({ ...jobForm, company: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="job-company-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Link type</label><select value={jobForm.link_type} onChange={(e) => setJobForm({ ...jobForm, link_type: e.target.value })} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="job-link-type-input"><option value="referral">Referral</option><option value="direct">Direct</option><option value="info">Info only</option></select></div>
                    </div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Link</label><Input value={jobForm.link} onChange={(e) => setJobForm({ ...jobForm, link: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="job-link-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Icon URL (optional)</label><Input value={jobForm.icon_url} onChange={(e) => setJobForm({ ...jobForm, icon_url: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="/icons/company.png or https://..." data-testid="job-icon-url-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Description</label><textarea value={jobForm.description} onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })} className="w-full h-28 bg-[#05050A] border border-white/10 rounded-md p-3 text-white resize-y" data-testid="job-description-input" /></div>
                    <div className="flex items-center gap-2"><input type="checkbox" checked={jobForm.active} onChange={(e) => setJobForm({ ...jobForm, active: e.target.checked })} data-testid="job-active-checkbox" /><label className="text-[#A1A1AA] text-sm">Active listing</label></div>
                    <div className="flex gap-3 pt-2">
                      <Button type="submit" className="cta-button rounded-full" data-testid="save-job-button">{editingJob ? "Update Job" : "Create Job"}</Button>
                      <Button type="button" variant="outline" onClick={() => setIsJobDialogOpen(false)} className="border-white/10">Cancel</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              <Button type="button" variant="outline" onClick={handleReimportJobs} className="border-white/10 text-white hover:bg-white/5" data-testid="reimport-jobs-button">Re-import sample jobs</Button>
              <Dialog open={isBulkDialogOpen} onOpenChange={(open) => { setIsBulkDialogOpen(open); if (!open) setBulkResult(null); }}>
                <DialogTrigger asChild>
                  <Button type="button" variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="bulk-upload-button">
                    <ListBullets size={16} className="mr-2" /> Bulk upload (CSV / XLSX)
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-xl">
                  <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>Bulk upload jobs</DialogTitle></DialogHeader>
                  <form onSubmit={handleBulkUpload} className="space-y-4 mt-4" data-testid="bulk-upload-form">
                    <div className="text-sm text-[#A1A1AA] leading-relaxed">
                      Upload a <span className="text-white">.csv</span> or <span className="text-white">.xlsx</span> file with these columns (case-insensitive):
                      <div className="font-mono text-xs mt-2 text-[#D9F839]">Job Title · Platform · Section · Domain · Duration · Pay · Location · Description · Link · Link Type · Company · Icon URL</div>
                      Required: <span className="text-white">Job Title, Platform, Link</span>. Rows matching an existing (platform + title) are skipped as duplicates.
                    </div>
                    <div>
                      <label className="text-[#A1A1AA] text-sm block mb-2">Default section (used when a row has no Section)</label>
                      <select value={bulkDefaultSection} onChange={(e) => setBulkDefaultSection(e.target.value)} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="bulk-default-section">
                        {SECTION_OPTIONS.filter((o) => o.value !== "all").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[#A1A1AA] text-sm block mb-2">Spreadsheet file</label>
                      <input type="file" name="file" accept=".csv,.tsv,.txt,.xlsx,.xlsm" required className="block w-full text-sm text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[#D9F839] file:text-black file:font-bold" data-testid="bulk-file-input" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="submit" disabled={bulkUploading} className="cta-button rounded-full disabled:opacity-60" data-testid="bulk-upload-submit">{bulkUploading ? "Uploading..." : "Upload"}</Button>
                      <Button type="button" variant="outline" onClick={downloadTemplateCsv} className="border-white/10" data-testid="download-template-button">Download template CSV</Button>
                    </div>
                    {bulkResult && (
                      <div className="panel-card p-4 text-sm" data-testid="bulk-upload-result">
                        <div><span className="text-[#D9F839]">Inserted:</span> {bulkResult.inserted}</div>
                        <div><span className="text-[#A1A1AA]">Skipped duplicates:</span> {bulkResult.skipped_duplicates}</div>
                        <div><span className="text-[#A1A1AA]">Total rows in file:</span> {bulkResult.total_rows}</div>
                        {bulkResult.errors?.length > 0 && (
                          <div className="mt-2">
                            <div className="text-red-400">Errors ({bulkResult.errors.length}):</div>
                            <ul className="list-disc pl-5 text-red-300 text-xs max-h-40 overflow-y-auto">
                              {bulkResult.errors.slice(0, 20).map((err, i) => <li key={i}>Row {err.row}: {err.error}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="panel-card p-4 mb-6 grid gap-4 md:grid-cols-[2fr,auto] md:items-center" data-testid="admin-jobs-note">
              <Input
                value={jobSearch}
                onChange={(e) => { setJobSearch(e.target.value); setJobPage(1); }}
                placeholder="Search jobs by title, platform, domain or source"
                className="bg-[#05050A] border-white/10 text-white"
                data-testid="admin-jobs-search"
              />
              <span className="text-sm text-[#A1A1AA]" data-testid="admin-jobs-count">
                {filteredAdminJobs.length} of {jobs.length} jobs
              </span>
            </div>

            <div className="bg-[#0E0E14] border border-white/10 rounded-3xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Title</TableHead>
                    <TableHead className="text-[#A1A1AA]">Platform</TableHead>
                    <TableHead className="text-[#A1A1AA]">Section</TableHead>
                    <TableHead className="text-[#A1A1AA]">Pay</TableHead>
                    <TableHead className="text-[#A1A1AA]">Source</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#A1A1AA] py-8">Loading...</TableCell></TableRow>
                  ) : jobs.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#A1A1AA] py-8">No jobs yet</TableCell></TableRow>
                  ) : (
                    visibleAdminJobs.map((job) => (
                      <TableRow key={job.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-job-row-${slugify(job.platform)}-${slugify(job.title)}`}>
                        <TableCell className="text-white font-medium">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const iconSrc = resolvePlatformIcon(job);
                              return iconSrc ? (
                                <img src={iconSrc} alt={job.platform} className="w-7 h-7 rounded object-contain bg-white/5 p-0.5 shrink-0" />
                              ) : (
                                <div className="w-7 h-7 rounded bg-[#D9F839]/10 text-[#D9F839] flex items-center justify-center text-[10px] font-mono shrink-0">
                                  {(job.platform || "?").slice(0, 2).toUpperCase()}
                                </div>
                              );
                            })()}
                            <span>{job.title}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#A1A1AA] text-sm">{job.platform}</TableCell>
                        <TableCell><span className="stats-chip">{sectionLabel(job.section)}</span></TableCell>
                        <TableCell className="text-[#D9F839] text-sm">{job.pay || "Not listed"}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-sm">{job.source}</TableCell>
                        <TableCell>{job.active ? <span className="flex items-center gap-1 text-green-500 text-sm"><Eye size={14} /> Active</span> : <span className="flex items-center gap-1 text-[#A1A1AA] text-sm"><EyeSlash size={14} /> Hidden</span>}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEditJob(job)} className="border-white/10 text-white hover:bg-white/5" data-testid={`edit-job-${job.id}`}><PencilSimple size={14} /></Button>
                            <Button size="sm" variant="outline" onClick={() => handleDeleteJob(job.id)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-job-${job.id}`}><Trash size={14} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <TablePagination
                page={adminJobPage}
                totalPages={adminTotalPages}
                total={filteredAdminJobs.length}
                onPage={(next) => setJobPage(Math.min(Math.max(1, next), adminTotalPages))}
                testId="admin-jobs-pagination"
              />

            </div>
          </TabsContent>

          <TabsContent value="posts">
            <div className="mb-6">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild><Button onClick={handleCreate} className="cta-button rounded-full" data-testid="create-post-button"><Plus size={18} className="mr-2" /> New Post</Button></DialogTrigger>
                <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>{editingPost ? "Edit Post" : "Create New Post"}</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Title</label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="post-title-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Slug (URL)</label><Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="post-slug-input" /></div>
                    </div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Excerpt</label><Input value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="post-excerpt-input" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Cover Image URL</label><Input value={formData.cover_image} onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="post-cover-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Video URL (optional)</label><Input value={formData.video_url} onChange={(e) => setFormData({ ...formData, video_url: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="YouTube or Vimeo URL" data-testid="post-video-input" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[#A1A1AA] text-sm block mb-2">Category</label>
                        <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                          <SelectTrigger className="bg-[#05050A] border-white/10"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent className="bg-[#0E0E14] border-white/10">{categories.map((category) => <SelectItem key={category.id} value={category.name}>{category.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Author</label><Input value={formData.author} onChange={(e) => setFormData({ ...formData, author: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="post-author-input" /></div>
                    </div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Tags (comma separated)</label><Input value={formData.tags} onChange={(e) => setFormData({ ...formData, tags: e.target.value })} className="bg-[#05050A] border-white/10" placeholder="AI, Training, Jobs" data-testid="post-tags-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Content (Markdown)</label><textarea value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full h-64 bg-[#05050A] border border-white/10 rounded-md p-3 text-white resize-y" required data-testid="post-content-input" /></div>
                    <div className="border border-white/10 rounded-2xl p-4">
                      <label className="text-[#D9F839] text-sm font-bold block mb-3">Referral Links / CTA Buttons</label>
                      {formData.referral_links.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {formData.referral_links.map((link, index) => (
                            <div key={index} className="flex items-center gap-2 p-2 bg-[#05050A] rounded-xl">
                              <div className="flex-1 min-w-0"><span className="text-white text-sm font-medium block truncate">{link.name}</span><span className="text-[#A1A1AA] text-xs truncate block">{link.url}</span></div>
                              {link.bonus && <span className="text-[#D9F839] text-xs px-2 py-1 bg-[#D9F839]/10 rounded-full">{link.bonus}</span>}
                              <Button type="button" size="sm" variant="ghost" onClick={() => removeReferralLink(index)} className="text-red-400 h-6 w-6 p-0 shrink-0" data-testid={`remove-referral-link-${index}`}><X size={12} /></Button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Input placeholder="Link Name" value={newReferralLink.name} onChange={(e) => setNewReferralLink({ ...newReferralLink, name: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-referral-name-input" />
                        <Input placeholder="URL" value={newReferralLink.url} onChange={(e) => setNewReferralLink({ ...newReferralLink, url: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-referral-url-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <Input placeholder="Bonus" value={newReferralLink.bonus} onChange={(e) => setNewReferralLink({ ...newReferralLink, bonus: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-referral-bonus-input" />
                        <Input placeholder="Description" value={newReferralLink.description} onChange={(e) => setNewReferralLink({ ...newReferralLink, description: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-referral-description-input" />
                      </div>
                      <Button type="button" onClick={addReferralLink} variant="outline" className="border-white/10 text-sm w-full" data-testid="add-referral-link-button"><Plus size={14} className="mr-1" /> Add Referral Link</Button>
                    </div>
                    <div className="flex items-center gap-2"><input type="checkbox" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} className="rounded" data-testid="post-published-checkbox" /><label className="text-[#A1A1AA] text-sm">Published</label></div>
                    <div className="flex gap-3 pt-4"><Button type="submit" className="cta-button rounded-full" data-testid="save-post-button">{editingPost ? "Update Post" : "Create Post"}</Button><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-white/10">Cancel</Button></div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-[#0E0E14] border border-white/10 rounded-3xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Title</TableHead>
                    <TableHead className="text-[#A1A1AA]">Author</TableHead>
                    <TableHead className="text-[#A1A1AA]">Category</TableHead>
                    <TableHead className="text-[#A1A1AA]">Links</TableHead>
                    <TableHead className="text-[#A1A1AA]">Date</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#A1A1AA] py-8">Loading...</TableCell></TableRow>
                  ) : posts.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center text-[#A1A1AA] py-8">No posts yet</TableCell></TableRow>
                  ) : (
                    posts.map((post) => (
                      <TableRow key={post.id} className="border-white/10 hover:bg-white/5" data-testid={`admin-post-row-${post.slug}`}>
                        <TableCell className="text-white font-medium">{post.title}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-sm">{post.author || "Anonymous AI Guru"}</TableCell>
                        <TableCell><span className="tag px-2 py-1 bg-[#D9F839]/10 text-[#D9F839] rounded-full text-xs">{post.category}</span></TableCell>
                        <TableCell>{post.referral_links?.length > 0 ? <span className="text-[#D9F839] text-sm">{post.referral_links.length} links</span> : <span className="text-[#A1A1AA] text-sm">-</span>}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-sm">{formatDate(post.created_at)}</TableCell>
                        <TableCell>{post.published ? <span className="flex items-center gap-1 text-green-500 text-sm"><Eye size={14} /> Published</span> : <span className="flex items-center gap-1 text-[#A1A1AA] text-sm"><EyeSlash size={14} /> Draft</span>}</TableCell>
                        <TableCell className="text-right"><div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => handleEdit(post)} className="border-white/10 text-white hover:bg-white/5" data-testid={`edit-post-${post.slug}`}><PencilSimple size={14} /></Button><Button size="sm" variant="outline" onClick={() => handleDelete(post.id)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-post-${post.slug}`}><Trash size={14} /></Button></div></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>


          <TabsContent value="newsletter">
            <div className="grid gap-6 lg:grid-cols-3 mb-6">
              <div className="panel-card p-5"><div className="text-xs uppercase text-[#A1A1AA] mb-2">Active subscribers</div><div className="text-3xl text-white" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="active-subscribers-count">{subscribers.filter((s) => s.active).length}</div></div>
              <div className="panel-card p-5"><div className="text-xs uppercase text-[#A1A1AA] mb-2">Total signups</div><div className="text-3xl text-white" style={{ fontFamily: "Unbounded, sans-serif" }} data-testid="total-subscribers-count">{subscribers.length}</div></div>
              <div className="panel-card p-5 flex flex-col justify-between gap-3">
                <div>
                  <div className="text-xs uppercase text-[#A1A1AA] mb-1">Weekly digest</div>
                  <div className="text-sm text-white">Auto-sends Mondays 09:00 UTC. Trigger now:</div>
                </div>
                <button onClick={handleSendWeeklyDigest} disabled={sendingWeekly} className="cta-button rounded-full px-4 py-2 text-sm flex items-center gap-2 self-start disabled:opacity-60" data-testid="send-weekly-digest-button">
                  <PaperPlaneTilt size={14} /> {sendingWeekly ? "Sending..." : "Send this week's digest"}
                </button>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <h3 className="text-white text-lg mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>New campaign</h3>
                <form onSubmit={handleCreateCampaign} className="panel-card p-5 space-y-3" data-testid="new-campaign-form">
                  <div>
                    <label className="text-[#A1A1AA] text-sm block mb-1">Subject</label>
                    <Input value={campaignForm.subject} onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="campaign-subject-input" />
                  </div>
                  <div>
                    <label className="text-[#A1A1AA] text-sm block mb-1">Body (HTML supported)</label>
                    <textarea value={campaignForm.body_html} onChange={(e) => setCampaignForm({ ...campaignForm, body_html: e.target.value })} rows={10} className="w-full bg-[#05050A] border border-white/10 rounded-md p-3 text-white font-mono text-sm" required data-testid="campaign-body-input" placeholder="<h1>Hello subscribers</h1><p>...</p>"></textarea>
                  </div>
                  <button type="submit" className="cta-button rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="save-campaign-button"><CheckCircle size={14} weight="bold" /> Save draft</button>
                </form>
                <div className="panel-card p-4 mt-3 text-sm text-[#A1A1AA]">
                  Test address to receive previews:
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="bg-[#05050A] border-white/10 mt-2" placeholder="you@example.com" data-testid="test-email-input" />
                </div>
              </div>

              <div>
                <h3 className="text-white text-lg mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>Campaign history</h3>
                <div className="panel-card p-4 max-h-[480px] overflow-y-auto" data-testid="campaign-history">
                  {campaigns.length === 0 ? (
                    <div className="text-[#A1A1AA] text-sm">No campaigns yet.</div>
                  ) : campaigns.map((c) => (
                    <div key={c.id} className="border-b border-white/5 py-3 last:border-b-0" data-testid={`campaign-row-${c.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-white text-sm font-medium truncate">{c.subject}</div>
                          <div className="text-xs text-[#A1A1AA]">{c.kind} · {c.status} · {c.sent_count || 0} sent · {new Date(c.created_at).toLocaleString()}</div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handleSendTest(c.id)} className="secondary-button rounded-full px-3 py-1.5 text-xs" data-testid={`send-test-${c.id}`}>Test</button>
                          {c.status !== "sent" && <button onClick={() => handleSendCampaign(c.id)} className="cta-button rounded-full px-3 py-1.5 text-xs" data-testid={`send-campaign-${c.id}`}>Send</button>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-white text-lg mb-3" style={{ fontFamily: "Unbounded, sans-serif" }}>Subscribers</h3>
              <div className="panel-card p-4 overflow-x-auto" data-testid="subscribers-table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-[#A1A1AA]">Email</TableHead>
                      <TableHead className="text-[#A1A1AA]">Source</TableHead>
                      <TableHead className="text-[#A1A1AA]">Status</TableHead>
                      <TableHead className="text-[#A1A1AA]">Subscribed</TableHead>
                      <TableHead className="text-[#A1A1AA] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscribers.length === 0 ? (
                      <TableRow className="border-white/10"><TableCell colSpan={5} className="text-center text-[#A1A1AA] py-10" data-testid="subscribers-empty">No subscribers yet.</TableCell></TableRow>
                    ) : subscribers.map((s) => (
                      <TableRow key={s.id} className="border-white/10 hover:bg-white/5" data-testid={`subscriber-row-${s.id}`}>
                        <TableCell className="text-white">{s.email}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-xs">{s.source}</TableCell>
                        <TableCell>
                          <span className={`stats-chip ${s.active ? "text-[#D9F839]" : ""}`}>{s.active ? "active" : "unsubscribed"}</span>
                        </TableCell>
                        <TableCell className="text-[#A1A1AA] text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => handleUnsubscribeSubscriber(s)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-subscriber-${s.id}`}><Trash size={14} /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>


          <TabsContent value="payments">
            <div className="panel-card p-4 mb-6 text-sm text-[#A1A1AA]" data-testid="admin-payments-note">
              Payments for paid job listings. A paid session automatically creates a sponsored job entry on the board.
            </div>
            <div className="stats-chip mb-4" data-testid="admin-payments-total">
              {payments.filter((p) => p.payment_status === "paid").length} paid · {payments.length} total
            </div>
            <div className="panel-card p-4 overflow-x-auto" data-testid="admin-payments-table-wrapper">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#A1A1AA]">Date</TableHead>
                    <TableHead className="text-[#A1A1AA]">Company</TableHead>
                    <TableHead className="text-[#A1A1AA]">Email</TableHead>
                    <TableHead className="text-[#A1A1AA]">Job title</TableHead>
                    <TableHead className="text-[#A1A1AA]">Package</TableHead>
                    <TableHead className="text-[#A1A1AA]">Amount</TableHead>
                    <TableHead className="text-[#A1A1AA]">Status</TableHead>
                    <TableHead className="text-[#A1A1AA]">Listing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 ? (
                    <TableRow className="border-white/10"><TableCell colSpan={8} className="text-center text-[#A1A1AA] py-10" data-testid="admin-payments-empty">No payments yet.</TableCell></TableRow>
                  ) : (
                    payments.map((p) => (
                      <TableRow key={p.session_id} className="border-white/10 hover:bg-white/5" data-testid={`admin-payment-row-${p.session_id}`}>
                        <TableCell className="text-white text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-white">{p.company_name}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-xs">{p.contact_email}</TableCell>
                        <TableCell className="text-white">{p.job_title}</TableCell>
                        <TableCell className="text-[#A1A1AA] text-xs">{p.package_id}</TableCell>
                        <TableCell className="text-[#D9F839]">${p.amount}</TableCell>
                        <TableCell>
                          <span className={`stats-chip ${p.payment_status === "paid" ? "text-[#D9F839]" : ""}`}>{p.payment_status}</span>
                        </TableCell>
                        <TableCell className="text-xs">{p.listing_created ? <span className="text-[#D9F839]">created</span> : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>



          <TabsContent value="categories">
            <div className="mb-6">
              <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
                <DialogTrigger asChild><Button className="cta-button rounded-full" data-testid="create-category-button"><Plus size={18} className="mr-2" /> New Category</Button></DialogTrigger>
                <DialogContent className="bg-[#0E0E14] border-white/10 text-white">
                  <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>Create Category</DialogTitle></DialogHeader>
                  <form onSubmit={handleCreateCategory} className="space-y-4 mt-4">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Name</label><Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="category-name-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Slug</label><Input value={categoryForm.slug} onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="category-slug-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Description</label><Input value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="category-description-input" /></div>
                    <Button type="submit" className="cta-button rounded-full" data-testid="save-category-button">Create Category</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-[#0E0E14] border border-white/10 rounded-3xl overflow-hidden">
              <Table>
                <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-[#A1A1AA]">Name</TableHead><TableHead className="text-[#A1A1AA]">Slug</TableHead><TableHead className="text-[#A1A1AA]">Description</TableHead><TableHead className="text-[#A1A1AA] text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id} className="border-white/10 hover:bg-white/5" data-testid={`category-row-${category.slug}`}>
                      <TableCell className="text-white font-medium">{category.name}</TableCell>
                      <TableCell className="text-[#A1A1AA] font-mono text-sm">{category.slug}</TableCell>
                      <TableCell className="text-[#A1A1AA] text-sm">{category.description}</TableCell>
                      <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => handleDeleteCategory(category.id)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-category-${category.slug}`}><Trash size={14} /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="homepage">
            <div className="grid gap-6">
              <form onSubmit={handleSaveHomepage} className="panel-card p-6" data-testid="homepage-content-form">
                <h3 className="text-lg text-white mb-1" style={{ fontFamily: "Unbounded, sans-serif" }}>Homepage content</h3>
                <p className="text-sm text-[#A1A1AA] mb-5">Edit the hero and every section heading shown on the public homepage.</p>
                <div className="grid gap-4 md:grid-cols-2">
                  {HOMEPAGE_FIELDS.map((field) => (
                    <div key={field.key} className={field.long ? "md:col-span-2" : ""}>
                      <label className="text-[#A1A1AA] text-sm block mb-2">{field.label}</label>
                      {field.long ? (
                        <Textarea
                          value={homepageForm[field.key] || ""}
                          onChange={(e) => setHomepageForm({ ...homepageForm, [field.key]: e.target.value })}
                          className="bg-[#05050A] border-white/10 text-white min-h-[70px]"
                          data-testid={`homepage-${field.key}-input`}
                        />
                      ) : (
                        <Input
                          value={homepageForm[field.key] || ""}
                          onChange={(e) => setHomepageForm({ ...homepageForm, [field.key]: e.target.value })}
                          className="bg-[#05050A] border-white/10 text-white"
                          data-testid={`homepage-${field.key}-input`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <Button type="submit" disabled={savingHomepage} className="cta-button rounded-full mt-6" data-testid="save-homepage-button">
                  {savingHomepage ? "Saving..." : "Save homepage content"}
                </Button>
              </form>

              {PLATFORM_SECTIONS.map((section) => {
                const entries = platformEntries.filter((entry) => entry.section === section.value);
                return (
                  <div className="panel-card p-6" key={section.value} data-testid={`homepage-section-${section.value}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div>
                        <h3 className="text-lg text-white" style={{ fontFamily: "Unbounded, sans-serif" }}>{section.label}</h3>
                        <p className="text-sm text-[#A1A1AA]">{entries.length} site{entries.length === 1 ? "" : "s"} on the homepage</p>
                      </div>
                      <Button type="button" onClick={() => openNewPlatform(section.value)} className="cta-button rounded-full" data-testid={`add-platform-${section.value}`}>
                        <Plus size={16} className="mr-2" /> Add site
                      </Button>
                    </div>
                    {entries.length === 0 ? (
                      <p className="text-sm text-[#A1A1AA]">No sites yet — add the first one.</p>
                    ) : (
                      <div className="grid gap-2">
                        {entries.map((entry) => (
                          <div className="flex flex-wrap items-center gap-3 bg-[#05050A] border border-white/10 rounded-2xl px-4 py-3" key={entry.id} data-testid={`platform-row-${slugify(entry.name)}`}>
                            <PlatformIcon badge={entry.badge} iconSrc={entry.icon} testId={`platform-row-icon-${slugify(entry.name)}`} />
                            <div className="min-w-0 flex-1">
                              <div className="text-white text-sm font-medium flex items-center gap-2 flex-wrap">
                                {entry.name}
                                <span className="stats-chip text-xs">{entry.action === "jobs" ? "See jobs" : "Visit platform"}</span>
                                {!entry.active && <span className="stats-chip text-xs opacity-70">Hidden</span>}
                              </div>
                              <div className="text-xs text-[#A1A1AA] truncate">{entry.description || entry.href || "—"}</div>
                            </div>
                            <span className="text-xs text-[#A1A1AA]">#{entry.order}</span>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => openEditPlatform(entry)} className="border-white/10 text-white hover:bg-white/5" data-testid={`edit-platform-${entry.id}`}><PencilSimple size={14} /></Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeletePlatform(entry.id)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-platform-${entry.id}`}><Trash size={14} /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <Dialog open={isPlatformDialogOpen} onOpenChange={setIsPlatformDialogOpen}>
              <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>{editingPlatform ? "Edit site" : "Add site"}</DialogTitle></DialogHeader>
                <form onSubmit={handlePlatformSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[#A1A1AA] text-sm block mb-2">Section</label>
                      <select value={platformForm.section} onChange={(e) => setPlatformForm({ ...platformForm, section: e.target.value })} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="platform-section-input">
                        {PLATFORM_SECTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[#A1A1AA] text-sm block mb-2">Button behaviour</label>
                      <select value={platformForm.action} onChange={(e) => setPlatformForm({ ...platformForm, action: e.target.value })} className="w-full h-10 bg-[#05050A] border border-white/10 rounded-md px-3" data-testid="platform-action-input">
                        <option value="jobs">See jobs (filters the jobs table)</option>
                        <option value="link">Visit platform (opens the link)</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Site name</label><Input value={platformForm.name} onChange={(e) => setPlatformForm({ ...platformForm, name: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="platform-name-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Jobs-table platform name</label><Input value={platformForm.platform} onChange={(e) => setPlatformForm({ ...platformForm, platform: e.target.value })} placeholder="Defaults to the site name" className="bg-[#05050A] border-white/10" data-testid="platform-platform-input" /></div>
                  </div>
                  <div><label className="text-[#A1A1AA] text-sm block mb-2">Description</label><Textarea value={platformForm.description} onChange={(e) => setPlatformForm({ ...platformForm, description: e.target.value })} className="bg-[#05050A] border-white/10 min-h-[70px]" data-testid="platform-description-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Logo URL or /icons/file.png</label><Input value={platformForm.icon} onChange={(e) => setPlatformForm({ ...platformForm, icon: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="platform-icon-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Monogram (fallback)</label><Input value={platformForm.badge} onChange={(e) => setPlatformForm({ ...platformForm, badge: e.target.value })} placeholder="Auto from name" maxLength={3} className="bg-[#05050A] border-white/10" data-testid="platform-badge-input" /></div>
                  </div>
                  <div><label className="text-[#A1A1AA] text-sm block mb-2">Signup / platform link</label><Input value={platformForm.href} onChange={(e) => setPlatformForm({ ...platformForm, href: e.target.value })} placeholder="https://..." className="bg-[#05050A] border-white/10" data-testid="platform-href-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Highlight note (optional)</label><Input value={platformForm.meta} onChange={(e) => setPlatformForm({ ...platformForm, meta: e.target.value })} placeholder="e.g. Signup code: ABC123" className="bg-[#05050A] border-white/10" data-testid="platform-meta-input" /></div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Display order</label><Input type="number" value={platformForm.order} onChange={(e) => setPlatformForm({ ...platformForm, order: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="platform-order-input" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
                    <input type="checkbox" checked={platformForm.active} onChange={(e) => setPlatformForm({ ...platformForm, active: e.target.checked })} data-testid="platform-active-input" />
                    Show on the homepage
                  </label>
                  <Button type="submit" className="cta-button rounded-full w-full" data-testid="save-platform-button">{editingPlatform ? "Save changes" : "Add to homepage"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>


          <TabsContent value="directory">
            <div className="panel-card p-6 mb-6" data-testid="directory-admin-panel">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div>
                  <h3 className="text-lg text-white" style={{ fontFamily: "Unbounded, sans-serif" }}>Directory article sites</h3>
                  <p className="text-sm text-[#A1A1AA]">Every site listed in "70 AI Training Sites: Complete Directory" with its clickable button.</p>
                </div>
                <Button type="button" onClick={openNewDirectorySite} className="cta-button rounded-full" data-testid="add-directory-site">
                  <Plus size={16} className="mr-2" /> Add site
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-[2fr,auto] md:items-center mt-4">
                <Input
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  placeholder="Search by site name, category or URL"
                  className="bg-[#05050A] border-white/10 text-white"
                  data-testid="directory-search-input"
                />
                <span className="text-sm text-[#A1A1AA]" data-testid="directory-admin-count">
                  {filteredDirectory.length} of {directorySites.length} sites
                </span>
              </div>
            </div>

            <div className="panel-card p-4 grid gap-2" data-testid="directory-admin-list">
              {filteredDirectory.length === 0 ? (
                <p className="text-sm text-[#A1A1AA] p-4">No sites match this search.</p>
              ) : (
                filteredDirectory.map((site) => (
                  <div className="flex flex-wrap items-center gap-3 bg-[#05050A] border border-white/10 rounded-2xl px-4 py-3" key={site.id} data-testid={`directory-admin-row-${site.id}`}>
                    <span className="text-xs text-[#7A7A85] w-8 tabular-nums">#{site.order}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-white text-sm flex items-center gap-2 flex-wrap">
                        {site.name}
                        <span className="stats-chip text-xs">{site.button_label || "Visit site"}</span>
                        {!site.active && <span className="stats-chip text-xs opacity-70">Hidden</span>}
                      </div>
                      <div className="text-xs text-[#A1A1AA] truncate">{site.category} · {site.url}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEditDirectorySite(site)} className="border-white/10 text-white hover:bg-white/5" data-testid={`edit-directory-${site.id}`}><PencilSimple size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDeleteDirectorySite(site.id)} className="border-white/10 text-red-400 hover:bg-red-500/10" data-testid={`delete-directory-${site.id}`}><Trash size={14} /></Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <Dialog open={isDirectoryDialogOpen} onOpenChange={setIsDirectoryDialogOpen}>
              <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-xl">
                <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>{editingDirectorySite ? "Edit directory site" : "Add directory site"}</DialogTitle></DialogHeader>
                <form onSubmit={handleDirectorySubmit} className="space-y-4 mt-4">
                  <div><label className="text-[#A1A1AA] text-sm block mb-2">Site name</label><Input value={directoryForm.name} onChange={(e) => setDirectoryForm({ ...directoryForm, name: e.target.value })} required className="bg-[#05050A] border-white/10" data-testid="directory-name-input" /></div>
                  <div><label className="text-[#A1A1AA] text-sm block mb-2">Link (button destination)</label><Input value={directoryForm.url} onChange={(e) => setDirectoryForm({ ...directoryForm, url: e.target.value })} required placeholder="https://..." className="bg-[#05050A] border-white/10" data-testid="directory-url-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Category heading</label><Input value={directoryForm.category} onChange={(e) => setDirectoryForm({ ...directoryForm, category: e.target.value })} list="directory-categories" className="bg-[#05050A] border-white/10" data-testid="directory-category-input" />
                      <datalist id="directory-categories">
                        {Array.from(new Set(directorySites.map((site) => site.category))).map((cat) => <option key={cat} value={cat} />)}
                      </datalist>
                    </div>
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Button label</label><Input value={directoryForm.button_label} onChange={(e) => setDirectoryForm({ ...directoryForm, button_label: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="directory-button-label-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 items-end">
                    <div><label className="text-[#A1A1AA] text-sm block mb-2">Display order</label><Input type="number" value={directoryForm.order} onChange={(e) => setDirectoryForm({ ...directoryForm, order: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="directory-order-input" /></div>
                    <label className="flex items-center gap-2 text-sm text-[#A1A1AA] pb-2">
                      <input type="checkbox" checked={directoryForm.active} onChange={(e) => setDirectoryForm({ ...directoryForm, active: e.target.checked })} data-testid="directory-active-input" />
                      Show in the article
                    </label>
                  </div>
                  <Button type="submit" className="cta-button rounded-full w-full" data-testid="save-directory-button">{editingDirectorySite ? "Save changes" : "Add site"}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>


          <TabsContent value="settings">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="panel-card p-6">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }}><Gear size={20} className="inline mr-2" /> Site Settings</h3>
                <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
                  <DialogTrigger asChild><Button variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="edit-site-settings-button">Edit Site Settings</Button></DialogTrigger>
                  <DialogContent className="bg-[#0E0E14] border-white/10 text-white max-w-lg">
                    <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>Site Settings</DialogTitle></DialogHeader>
                    <form onSubmit={handleSaveSettings} className="space-y-4 mt-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Site Name</label><Input value={settingsForm.site_name} onChange={(e) => setSettingsForm({ ...settingsForm, site_name: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="settings-site-name-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Tagline</label><Input value={settingsForm.tagline} onChange={(e) => setSettingsForm({ ...settingsForm, tagline: e.target.value })} className="bg-[#05050A] border-white/10" data-testid="settings-tagline-input" /></div>
                      <div>
                        <label className="text-[#A1A1AA] text-sm block mb-2">Menu Items</label>
                        <div className="space-y-2 mb-3">
                          {settingsForm.menu_items.map((item, index) => (
                            <div key={`${item.label}-${index}`} className="flex items-center gap-2 p-2 bg-[#05050A] rounded-xl">
                              <span className="text-white text-sm flex-1">{item.label}</span>
                              <span className="text-[#A1A1AA] text-xs">{item.url}</span>
                              <Button type="button" size="sm" variant="ghost" onClick={() => removeMenuItem(index)} className="text-red-400 h-6 w-6 p-0" data-testid={`remove-menu-item-${index}`}><X size={12} /></Button>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input placeholder="Label" value={newMenuItem.label} onChange={(e) => setNewMenuItem({ ...newMenuItem, label: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-menu-label-input" />
                          <Input placeholder="URL" value={newMenuItem.url} onChange={(e) => setNewMenuItem({ ...newMenuItem, url: e.target.value })} className="bg-[#05050A] border-white/10 text-sm" data-testid="new-menu-url-input" />
                          <Button type="button" onClick={addMenuItem} variant="outline" className="border-white/10" data-testid="add-menu-item-button"><Plus size={14} /></Button>
                        </div>
                      </div>
                      <Button type="submit" className="cta-button rounded-full" data-testid="save-settings-button">Save Settings</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <div className="mt-4 text-sm text-[#A1A1AA]" data-testid="site-settings-summary"><p><strong className="text-white">Name:</strong> {settings?.site_name}</p><p><strong className="text-white">Tagline:</strong> {settings?.tagline}</p><p><strong className="text-white">Menu Items:</strong> {settings?.menu_items?.length || 0}</p></div>
              </div>

              <div className="panel-card p-6">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: "Unbounded, sans-serif" }}><Lock size={20} className="inline mr-2" /> Security</h3>
                <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
                  <DialogTrigger asChild><Button variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="change-password-button">Change Password</Button></DialogTrigger>
                  <DialogContent className="bg-[#0E0E14] border-white/10 text-white">
                    <DialogHeader><DialogTitle style={{ fontFamily: "Unbounded, sans-serif" }}>Change Password</DialogTitle></DialogHeader>
                    <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Current Password</label><Input type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="current-password-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">New Password</label><Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="new-password-input" /></div>
                      <div><label className="text-[#A1A1AA] text-sm block mb-2">Confirm New Password</label><Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} className="bg-[#05050A] border-white/10" required data-testid="confirm-password-input" /></div>
                      <Button type="submit" className="cta-button rounded-full" data-testid="update-password-button">Update Password</Button>
                    </form>
                  </DialogContent>
                </Dialog>
                <p className="mt-4 text-sm text-[#A1A1AA]">Keep your account secure by using a strong password.</p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" toastOptions={{ style: { background: "#0E0E14", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" } }} />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/post/:slug" element={<BlogPostPage />} />
            <Route path="/post-a-job" element={<PostAJobPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/newsletter" element={<NewsletterPage />} />
            <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
            <Route path="/about" element={<><Header /><AboutPage /><SiteFooter /></>} />
            <Route path="/contact" element={<><Header /><ContactPage /><SiteFooter /></>} />
            <Route path="/privacy-policy" element={<><Header /><PrivacyPolicyPage /><SiteFooter /></>} />
            <Route path="/terms" element={<><Header /><TermsPage /><SiteFooter /></>} />
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;